/**
 * UI wiring — input source picker, look controls, presets, recorder, meters.
 *
 * Pure DOM, no framework. Receives an injected dependency object so tests
 * (and future UIs) can stub the engine/renderer.
 */

import { AudioEngine } from "../audio/sources";
import type { AnalysisJson } from "../audio/preanalyzed";
import { Renderer } from "../viz/renderer";
import type { SceneType } from "../viz/renderer";
import { ALT_SCENE_LABELS } from "../viz/altScenes";
import type { Modulation, ModulationMode, ModulationEasing } from "../viz/modulations";
import { DEFAULT_MODULATION } from "../viz/modulations";
import type { BackgroundRenderer, BgStyle } from "../viz/background";
import {
  CanvasRecorder,
  downloadBlob,
  suggestFilename,
} from "../record/capture";
import { RecordingCompositor } from "../record/recordingCompositor";
import {
  SOCIAL_PRESETS,
  getSocialPreset,
  suggestSocialFilename,
} from "../record/socialPresets";
import { presets, buildShareUrl } from "../presets/store";
import { VIZZ_PRESETS, applyVizPreset } from "../presets/vizzLibrary";
import type { ApplyOptions, VizPreset } from "../presets/vizzLibrary";
import { VIZZ_EXTRAS } from "../presets/vizzCameraStates";
import { createMorphState, startMorph, cancelMorph, tickMorph, EasingName } from "../presets/morph";
import { createPadContainer, type PadContainerHandle, type PadAudioFeatures } from "./padContainer";
import { ImageOverlay } from "../viz/imageOverlay";
import { buildLookPanel, FRAMING_PRESETS } from "./lookPanel";
import { CONCEPT_MODES, ConceptMode, BioclastParams } from "../viz/scenes/bioclast";
import { initAudioProcessingPanel } from "./audioProcessingPanel";
import {
  NowPlayingOverlay,
  OverlayConfig,
  OverlayPosition,
  FontFamily,
  FontWeight,
  LyricMode,
  DEFAULT_OVERLAY,
} from "./nowPlaying";

interface Deps {
  engine: AudioEngine;
  renderer: Renderer;
  background?: BackgroundRenderer | null;
}

interface Refs {
  toggle: HTMLButtonElement;
  close: HTMLButtonElement;
  panel: HTMLElement;
  sourceStatus: HTMLElement;
  sourceTabBtn: HTMLButtonElement;
  inputDevice: HTMLSelectElement;
  playPauseAudio: HTMLButtonElement;
  mediaUrlInput: HTMLInputElement;
  mediaUrlLoad: HTMLButtonElement;
  clipEnabled: HTMLInputElement;
  clipTrack: HTMLElement;
  clipWaveCanvas: HTMLCanvasElement;
  clipPlayed: HTMLElement;
  clipSelection: HTMLElement;
  clipHandleStart: HTMLButtonElement;
  clipHandleEnd: HTMLButtonElement;
  clipScrubber: HTMLElement;
  clipPlayhead: HTMLInputElement;
  clipStart: HTMLInputElement;
  clipEnd: HTMLInputElement;
  clipSetStart: HTMLButtonElement;
  clipSetEnd: HTMLButtonElement;
  clipPreview: HTMLButtonElement;
  clipTimeReadout: HTMLElement;
  mobileHint: HTMLElement;
  fileInput: HTMLInputElement;
  analysisInput: HTMLInputElement;
  lookPanel: HTMLElement;
  savePreset: HTMLButtonElement;
  presetList: HTMLSelectElement;
  exportPresets: HTMLButtonElement;
  importPresets: HTMLInputElement;
  sharePresetLink: HTMLButtonElement;
  sharePresetStatus: HTMLElement;
  brandKitSave: HTMLButtonElement;
  brandKitApply: HTMLButtonElement;
  brandKitStatus: HTMLElement;
  randomizeLook: HTMLButtonElement;
  autoSwitchSilence: HTMLInputElement;
  postFxEnabled: HTMLInputElement;
  postFxClearAll: HTMLButtonElement;
  postFxSliders: HTMLElement;
  cameraMotionReset: HTMLButtonElement;
  cameraMotionSliders: HTMLElement;
  bgStyle: HTMLSelectElement;
  bgPrimary: HTMLInputElement;
  bgSecondary: HTMLInputElement;
  bgReactivity: HTMLInputElement;
  visualizerGrid: HTMLElement;
  modParam: HTMLSelectElement;
  modMode: HTMLSelectElement;
  modAdd: HTMLButtonElement;
  modList: HTMLElement;
  vizzLibraryGroup: HTMLSelectElement;
  vizzLibraryRandom: HTMLButtonElement;
  vizzLibraryList: HTMLElement;
  morphDuration: HTMLInputElement;
  morphEasing: HTMLSelectElement;
  morphCancel: HTMLButtonElement;
  vizzReplaceLook: HTMLInputElement;
  vizzApplyLook: HTMLInputElement;
  vizzApplyScene: HTMLInputElement;
  vizzApplyCamera: HTMLInputElement;
  vizzApplyBg: HTMLInputElement;
  vizzApplyFx: HTMLInputElement;
  vizzApplyAll: HTMLButtonElement;
  vizzApplyLookOnly: HTMLButtonElement;
  vizzApplyLookCam: HTMLButtonElement;
  vizzFreeCamera: HTMLButtonElement;
  imgOverlay: {
    upload: HTMLInputElement;
    clear: HTMLButtonElement;
    visible: HTMLInputElement;
    opacity: HTMLInputElement;
    blend: HTMLSelectElement;
    scale: HTMLInputElement;
    posX: HTMLInputElement;
    posY: HTMLInputElement;
  };
  recordBtn: HTMLButtonElement;
  snapshotBtn: HTMLButtonElement;
  recordStatus: HTMLElement;
  aspectSelect: HTMLSelectElement;
  reelLength: HTMLSelectElement;
  autoCycle: HTMLInputElement;
  frame: HTMLElement;
  overlay: {
    enabled: HTMLInputElement;
    title: HTMLInputElement;
    artist: HTMLInputElement;
    album: HTMLInputElement;
    position: HTMLSelectElement;
    showLyrics: HTMLInputElement;
    lyricMode: HTMLSelectElement;
    lyrics: HTMLTextAreaElement;
    lyricsFile: HTMLInputElement;
    artFile: HTMLInputElement;
    artLabel: HTMLElement;
    artClear: HTMLButtonElement;
    font: HTMLSelectElement;
    weight: HTMLSelectElement;
    uppercase: HTMLInputElement;
    watermarkEnabled: HTMLInputElement;
    watermarkHandle: HTMLInputElement;
    watermarkLogoFile: HTMLInputElement;
    watermarkLogoLabel: HTMLElement;
    watermarkLogoClear: HTMLButtonElement;
    watermarkPosition: HTMLSelectElement;
  };
  meters: {
    bass: HTMLElement;
    mid: HTMLElement;
    treble: HTMLElement;
    beat: HTMLElement;
  };
  bpm: HTMLElement;
}

function $<T extends Element>(sel: string): T {
  const el = document.querySelector<T>(sel);
  if (!el) throw new Error(`Missing element: ${sel}`);
  return el;
}

function getRefs(): Refs {
  return {
    toggle: $<HTMLButtonElement>("#toggleControls"),
    close: $<HTMLButtonElement>("#closeControls"),
    panel: $<HTMLElement>("#controls"),
    sourceStatus: $<HTMLElement>("#sourceStatus"),
    sourceTabBtn: $<HTMLButtonElement>("#sourceTabBtn"),
    inputDevice: $<HTMLSelectElement>("#inputDevice"),
    playPauseAudio: $<HTMLButtonElement>("#playPauseAudio"),
    mediaUrlInput: $<HTMLInputElement>("#mediaUrlInput"),
    mediaUrlLoad: $<HTMLButtonElement>("#mediaUrlLoad"),
    clipEnabled: $<HTMLInputElement>("#clipEnabled"),
    clipTrack: $<HTMLElement>("#clipTrack"),
    clipWaveCanvas: $<HTMLCanvasElement>("#clipWaveCanvas"),
    clipPlayed: $<HTMLElement>("#clipPlayed"),
    clipSelection: $<HTMLElement>("#clipSelection"),
    clipHandleStart: $<HTMLButtonElement>("#clipHandleStart"),
    clipHandleEnd: $<HTMLButtonElement>("#clipHandleEnd"),
    clipScrubber: $<HTMLElement>("#clipScrubber"),
    clipPlayhead: $<HTMLInputElement>("#clipPlayhead"),
    clipStart: $<HTMLInputElement>("#clipStart"),
    clipEnd: $<HTMLInputElement>("#clipEnd"),
    clipSetStart: $<HTMLButtonElement>("#clipSetStart"),
    clipSetEnd: $<HTMLButtonElement>("#clipSetEnd"),
    clipPreview: $<HTMLButtonElement>("#clipPreview"),
    clipTimeReadout: $<HTMLElement>("#clipTimeReadout"),
    mobileHint: $<HTMLElement>("#mobileHint"),
    fileInput: $<HTMLInputElement>("#fileInput"),
    analysisInput: $<HTMLInputElement>("#analysisInput"),
    lookPanel: $<HTMLElement>("#lookPanel"),
    savePreset: $<HTMLButtonElement>("#savePreset"),
    presetList: $<HTMLSelectElement>("#presetList"),
    exportPresets: $<HTMLButtonElement>("#exportPresets"),
    importPresets: $<HTMLInputElement>("#importPresets"),
    sharePresetLink: $<HTMLButtonElement>("#sharePresetLink"),
    sharePresetStatus: $<HTMLElement>("#sharePresetStatus"),
    brandKitSave: $<HTMLButtonElement>("#brandKitSave"),
    brandKitApply: $<HTMLButtonElement>("#brandKitApply"),
    brandKitStatus: $<HTMLElement>("#brandKitStatus"),
    randomizeLook: $<HTMLButtonElement>("#randomizeLook"),
    autoSwitchSilence: $<HTMLInputElement>("#autoSwitchSilence"),
    postFxEnabled: $<HTMLInputElement>("#postFxEnabled"),
    postFxClearAll: $<HTMLButtonElement>("#postFxClearAll"),
    postFxSliders: $<HTMLElement>("#postFxSliders"),
    cameraMotionReset: $<HTMLButtonElement>("#cameraMotionReset"),
    cameraMotionSliders: $<HTMLElement>("#cameraMotionSliders"),
    bgStyle: $<HTMLSelectElement>("#bgStyle"),
    bgPrimary: $<HTMLInputElement>("#bgPrimary"),
    bgSecondary: $<HTMLInputElement>("#bgSecondary"),
    bgReactivity: $<HTMLInputElement>("#bgReactivity"),
    visualizerGrid: $<HTMLElement>("#visualizerGrid"),
    modParam: $<HTMLSelectElement>("#modParam"),
    modMode: $<HTMLSelectElement>("#modMode"),
    modAdd: $<HTMLButtonElement>("#modAdd"),
    modList: $<HTMLElement>("#modList"),
    vizzLibraryGroup: $<HTMLSelectElement>("#vizzLibraryGroup"),
    vizzLibraryRandom: $<HTMLButtonElement>("#vizzLibraryRandom"),
    vizzLibraryList: $<HTMLElement>("#vizzLibraryList"),
    morphDuration: $<HTMLInputElement>("#morphDuration"),
    morphEasing: $<HTMLSelectElement>("#morphEasing"),
    morphCancel: $<HTMLButtonElement>("#morphCancel"),
    vizzReplaceLook: $<HTMLInputElement>("#vizzReplaceLook"),
    vizzApplyLook: $<HTMLInputElement>("#vizzApplyLook"),
    vizzApplyScene: $<HTMLInputElement>("#vizzApplyScene"),
    vizzApplyCamera: $<HTMLInputElement>("#vizzApplyCamera"),
    vizzApplyBg: $<HTMLInputElement>("#vizzApplyBg"),
    vizzApplyFx: $<HTMLInputElement>("#vizzApplyFx"),
    vizzApplyAll: $<HTMLButtonElement>("#vizzApplyAll"),
    vizzApplyLookOnly: $<HTMLButtonElement>("#vizzApplyLookOnly"),
    vizzApplyLookCam: $<HTMLButtonElement>("#vizzApplyLookCam"),
    vizzFreeCamera: $<HTMLButtonElement>("#vizzFreeCamera"),
    imgOverlay: {
      upload: $<HTMLInputElement>("#overlayUpload"),
      clear: $<HTMLButtonElement>("#overlayClear"),
      visible: $<HTMLInputElement>("#overlayVisible"),
      opacity: $<HTMLInputElement>("#overlayOpacity"),
      blend: $<HTMLSelectElement>("#overlayBlend"),
      scale: $<HTMLInputElement>("#overlayScale"),
      posX: $<HTMLInputElement>("#overlayPosX"),
      posY: $<HTMLInputElement>("#overlayPosY"),
    },
    recordBtn: $<HTMLButtonElement>("#recordBtn"),
    snapshotBtn: $<HTMLButtonElement>("#snapshotBtn"),
    recordStatus: $<HTMLElement>("#recordStatus"),
    aspectSelect: $<HTMLSelectElement>("#aspect"),
    reelLength: $<HTMLSelectElement>("#reelLength"),
    autoCycle: $<HTMLInputElement>("#autoCycle"),
    frame: $<HTMLElement>("#frame"),
    overlay: {
      enabled: $<HTMLInputElement>("#overlayEnabled"),
      title: $<HTMLInputElement>("#overlayTitle"),
      artist: $<HTMLInputElement>("#overlayArtist"),
      album: $<HTMLInputElement>("#overlayAlbum"),
      position: $<HTMLSelectElement>("#overlayPosition"),
      showLyrics: $<HTMLInputElement>("#overlayShowLyrics"),
      lyricMode: $<HTMLSelectElement>("#overlayLyricMode"),
      lyrics: $<HTMLTextAreaElement>("#overlayLyrics"),
      lyricsFile: $<HTMLInputElement>("#overlayLyricsFile"),
      artFile: $<HTMLInputElement>("#overlayArtFile"),
      artLabel: $<HTMLElement>("#overlayArtLabel"),
      artClear: $<HTMLButtonElement>("#overlayArtClear"),
      font: $<HTMLSelectElement>("#overlayFont"),
      weight: $<HTMLSelectElement>("#overlayWeight"),
      uppercase: $<HTMLInputElement>("#overlayUppercase"),
      watermarkEnabled: $<HTMLInputElement>("#watermarkEnabled"),
      watermarkHandle: $<HTMLInputElement>("#watermarkHandle"),
      watermarkLogoFile: $<HTMLInputElement>("#watermarkLogoFile"),
      watermarkLogoLabel: $<HTMLElement>("#watermarkLogoLabel"),
      watermarkLogoClear: $<HTMLButtonElement>("#watermarkLogoClear"),
      watermarkPosition: $<HTMLSelectElement>("#watermarkPosition"),
    },
    meters: {
      bass: $<HTMLElement>("#meter-bass"),
      mid: $<HTMLElement>("#meter-mid"),
      treble: $<HTMLElement>("#meter-treble"),
      beat: $<HTMLElement>("#meter-beat"),
    },
    bpm: $<HTMLElement>("#bpmReadout"),
  };
}

export function initUI(deps: Deps): void {
  const refs = getRefs();
  const { engine, renderer } = deps;
  const background = deps.background ?? null;
  const recorder = new CanvasRecorder();
  let pendingAnalysis: AnalysisJson | null = null;

  // Universal Audio Processing sliders in the Source tab. Safe to call
  // even if the DOM elements aren't present (older templates).
  initAudioProcessingPanel();

  // -------- Panel show/hide --------
  const setPanel = (open: boolean): void => {
    refs.panel.classList.toggle("hidden", !open);
    refs.panel.setAttribute("aria-hidden", open ? "false" : "true");
    refs.toggle.setAttribute("aria-expanded", open ? "true" : "false");
  };
  refs.toggle.addEventListener("click", () =>
    setPanel(refs.panel.classList.contains("hidden")),
  );
  refs.close.addEventListener("click", () => setPanel(false));
  // Open by default on first load
  setPanel(true);

  // -------- Lightweight workflow status --------
  // Keep the production-style vertical workflow calm. Only surface status when
  // a Full Recipe locks the camera, because that affects manual control.
  const cameraLockBannerEl = document.getElementById("cameraLockBanner");
  const statusSceneEl = document.getElementById("statusScene");
  const statusRecipeEl = document.getElementById("statusRecipe");
  const statusCameraEl = document.getElementById("statusCamera");
  const sceneLabel = (type: SceneType): string => (
    type === "bioclast" ? "Bioclast" : ALT_SCENE_LABELS[type]
  );
  const updateSceneStatus = (type = renderer.sceneType): void => {
    if (statusSceneEl) statusSceneEl.textContent = `Scene: ${sceneLabel(type)}`;
  };
  const setRecipeStatus = (text: string): void => {
    if (statusRecipeEl) statusRecipeEl.textContent = text;
  };
  let pendingCameraLockLabel: string | null = null;
  let currentCameraLockLabel: string | null = null;
  const setCameraStatus = (label: string | null): void => {
    currentCameraLockLabel = label;
    if (statusCameraEl) {
      statusCameraEl.textContent = label ? `Camera locked by ${label}.` : "Camera free.";
    }
    if (cameraLockBannerEl) cameraLockBannerEl.hidden = !label;
    refs.vizzFreeCamera.hidden = !label;
  };
  renderer.onAltCameraOverrideChange = (locked) => {
    setCameraStatus(locked ? pendingCameraLockLabel ?? currentCameraLockLabel ?? "recipe" : null);
  };
  updateSceneStatus();
  setCameraStatus(null);

  // -------- Source picker --------
  const setStatus = (msg: string): void => {
    refs.sourceStatus.textContent = msg;
  };

  const isLikelyAudioUrl = (url: string): boolean => {
    if (/\.(mp3|wav|ogg|m4a|aac|flac)(\?|#|$)/i.test(url)) return true;
    if (/soundcloud\.com\//i.test(url)) return false;
    return /^https?:\/\//i.test(url);
  };

  const normalizeSharedMediaUrls = (url: URL): string[] => {
    const out = new Set<string>();
    const host = url.hostname.replace(/^www\./, "").toLowerCase();
    out.add(url.toString());

    // Dropbox share links -> direct file variants.
    if (host === "dropbox.com") {
      const a = new URL(url.toString());
      a.searchParams.set("dl", "1");
      out.add(a.toString());
      const b = new URL(url.toString());
      b.hostname = "dl.dropboxusercontent.com";
      b.searchParams.delete("dl");
      out.add(b.toString());
    }

    // Google Drive file links -> direct download endpoint.
    if (host === "drive.google.com") {
      const pathMatch = url.pathname.match(/\/file\/d\/([^/]+)/);
      const id = pathMatch?.[1] ?? url.searchParams.get("id");
      if (id) {
        const direct = new URL("https://drive.google.com/uc");
        direct.searchParams.set("export", "download");
        direct.searchParams.set("id", id);
        out.add(direct.toString());
      }
    }

    return [...out];
  };

  const urlHostLabel = (url: string): string => {
    try {
      return new URL(url).hostname.replace(/^www\./, "");
    } catch {
      return "remote";
    }
  };

  const tryLoadRemoteCandidate = (candidate: string): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      let done = false;
      const finish = (ok: boolean): void => {
        if (done) return;
        done = true;
        resolve(ok);
      };
      try {
        const info = engine.useRemoteUrl(candidate, `URL: ${urlHostLabel(candidate)}`);
        const el = info.mediaElement;
        if (!el) {
          finish(false);
          return;
        }

        const ok = (): void => {
          cleanup();
          finish(true);
        };
        const fail = (): void => {
          cleanup();
          finish(false);
        };
        const cleanup = (): void => {
          el.removeEventListener("loadedmetadata", ok);
          el.removeEventListener("canplay", ok);
          el.removeEventListener("error", fail);
          clearTimeout(timer);
        };

        el.addEventListener("loadedmetadata", ok, { once: true });
        el.addEventListener("canplay", ok, { once: true });
        el.addEventListener("error", fail, { once: true });
        const timer = setTimeout(fail, 7000);
        void el.play().catch(() => {
          // iOS autoplay policy may block; metadata can still load.
        });
      } catch {
        finish(false);
      }
    });
  };

  const isKnownUnsupportedMediaPage = (url: string): boolean => {
    return /youtube\.com|youtu\.be|spotify\.com|bandcamp\.com/i.test(url);
  };

  let clipStartSec = 0;
  let clipEndSec = 0;
  let clipPreviewing = false;
  let clipWavePeaks: number[] | null = null;

  type ClipDragMode = "start" | "end" | "window" | "scrub" | null;
  let clipDragMode: ClipDragMode = null;
  let clipDragAnchorSec = 0;
  let clipDragStartBase = 0;
  let clipDragEndBase = 0;

  const formatClipClock = (sec: number): string => {
    const s = Math.max(0, sec);
    const m = Math.floor(s / 60);
    const r = s - m * 60;
    return `${m}:${r.toFixed(1).padStart(4, "0")}`;
  };

  const clampClipWindow = (duration: number): void => {
    if (!Number.isFinite(duration) || duration <= 0) {
      clipStartSec = 0;
      clipEndSec = 0;
      return;
    }
    clipStartSec = Math.max(0, Math.min(clipStartSec, duration));
    clipEndSec = Math.max(0, Math.min(clipEndSec || duration, duration));
    if (clipEndSec <= clipStartSec) {
      clipEndSec = Math.min(duration, clipStartSec + 0.1);
    }
  };

  const drawWaveform = (peaks: number[] | null): void => {
    const canvas = refs.clipWaveCanvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    const cssW = Math.max(1, Math.floor(canvas.clientWidth || 1));
    const cssH = Math.max(1, Math.floor(canvas.clientHeight || 1));
    const pxW = Math.max(1, Math.floor(cssW * dpr));
    const pxH = Math.max(1, Math.floor(cssH * dpr));
    if (canvas.width !== pxW || canvas.height !== pxH) {
      canvas.width = pxW;
      canvas.height = pxH;
    }

    ctx.clearRect(0, 0, pxW, pxH);
    const grad = ctx.createLinearGradient(0, 0, 0, pxH);
    grad.addColorStop(0, "rgba(14, 28, 46, 0.35)");
    grad.addColorStop(1, "rgba(5, 10, 20, 0.75)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, pxW, pxH);

    const source = peaks && peaks.length > 0
      ? peaks
      : Array.from({ length: 180 }, (_, i) => {
        const f = i / 179;
        return 0.14 + Math.abs(Math.sin(f * Math.PI * 9)) * 0.44;
      });
    const bars = source.length;
    const barW = pxW / bars;
    const centerY = pxH * 0.5;
    ctx.fillStyle = "rgba(132, 214, 255, 0.88)";
    for (let i = 0; i < bars; i++) {
      const amp = Math.max(0.02, Math.min(1, source[i]));
      const h = amp * (pxH * 0.42);
      const x = i * barW;
      const w = Math.max(1, barW * 0.68);
      ctx.fillRect(x, centerY - h, w, h * 2);
    }
  };

  const extractWavePeaksFromFile = async (file: File): Promise<number[] | null> => {
    try {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      const ac = new Ctor();
      const data = await file.arrayBuffer();
      const audioBuf = await ac.decodeAudioData(data.slice(0));
      const peaksCount = 220;
      const channels = audioBuf.numberOfChannels;
      const length = audioBuf.length;
      const step = Math.max(1, Math.floor(length / peaksCount));
      const peaks = new Array<number>(peaksCount).fill(0);

      for (let i = 0; i < peaksCount; i++) {
        const start = i * step;
        const end = Math.min(length, start + step);
        let peak = 0;
        for (let c = 0; c < channels; c++) {
          const ch = audioBuf.getChannelData(c);
          for (let j = start; j < end; j++) {
            const v = Math.abs(ch[j]);
            if (v > peak) peak = v;
          }
        }
        peaks[i] = peak;
      }
      const maxPeak = Math.max(...peaks, 0.0001);
      const normalized = peaks.map((p) => Math.pow(p / maxPeak, 0.72));
      void ac.close();
      return normalized;
    } catch {
      return null;
    }
  };

  const syncClipTrackVisuals = (): void => {
    const media = engine.info().mediaElement;
    const duration = media?.duration ?? 0;
    const hasDuration = Number.isFinite(duration) && duration > 0;
    const cur = hasDuration ? Math.max(0, Math.min(media?.currentTime ?? 0, duration)) : 0;
    const startPct = hasDuration ? (clipStartSec / duration) * 100 : 0;
    const endPct = hasDuration ? (clipEndSec / duration) * 100 : 0;
    const playPct = hasDuration ? (cur / duration) * 100 : 0;

    refs.clipSelection.style.left = `${startPct}%`;
    refs.clipSelection.style.width = `${Math.max(0, endPct - startPct)}%`;
    refs.clipHandleStart.style.left = `${startPct}%`;
    refs.clipHandleEnd.style.left = `${endPct}%`;
    refs.clipScrubber.style.left = `${playPct}%`;
    refs.clipPlayed.style.width = `${playPct}%`;
    refs.clipTrack.setAttribute("aria-valuenow", String(Math.round(playPct)));
  };

  const trackPointerToTime = (clientX: number): number => {
    const media = engine.info().mediaElement;
    const duration = media?.duration ?? 0;
    if (!Number.isFinite(duration) || duration <= 0) return 0;
    const rect = refs.clipTrack.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / Math.max(1, rect.width)));
    return pct * duration;
  };

  const syncClipUi = (): void => {
    const media = engine.info().mediaElement;
    const hasMedia = Boolean(media);
    const duration = media?.duration ?? 0;
    const enabled = hasMedia && Number.isFinite(duration) && duration > 0;

    if (enabled) {
      clampClipWindow(duration);
      const max = duration.toFixed(2);
      refs.clipPlayhead.max = max;
      refs.clipStart.max = max;
      refs.clipEnd.max = max;
      refs.clipPlayhead.value = String(
        Math.max(0, Math.min(media?.currentTime ?? 0, duration)),
      );
      refs.clipStart.value = String(clipStartSec);
      refs.clipEnd.value = String(clipEndSec);
      refs.clipTimeReadout.textContent =
        `Start ${formatClipClock(clipStartSec)} · End ${formatClipClock(clipEndSec)} · ` +
        `Length ${formatClipClock(Math.max(0, clipEndSec - clipStartSec))}`;
    } else {
      refs.clipPlayhead.value = "0";
      refs.clipStart.value = "0";
      refs.clipEnd.value = "0";
      refs.clipTimeReadout.textContent = "Load audio to edit clip range.";
    }

    refs.clipEnabled.disabled = !enabled;
    refs.clipTrack.style.opacity = enabled ? "1" : "0.45";
    refs.clipPlayhead.disabled = !enabled;
    refs.clipStart.disabled = !enabled;
    refs.clipEnd.disabled = !enabled;
    refs.clipSetStart.disabled = !enabled;
    refs.clipSetEnd.disabled = !enabled;
    refs.clipPreview.disabled = !enabled;
    syncClipTrackVisuals();
  };

  const refreshSourceDependentUi = (): void => {
    refreshPlayButton();
    syncClipUi();
  };

  const applyLoadedMedia = (): void => {
    const el = engine.info().mediaElement;
    if (!el) {
      refreshSourceDependentUi();
      return;
    }

    const onMeta = (): void => {
      if (Number.isFinite(el.duration) && el.duration > 0) {
        clipStartSec = 0;
        clipEndSec = el.duration;
      }
      syncClipUi();
    };
    el.addEventListener("loadedmetadata", onMeta, { once: true });
    if (Number.isFinite(el.duration) && el.duration > 0) onMeta();
    refreshSourceDependentUi();
  };

  const attachClipTrackPointerUi = (): void => {
    refs.clipTrack.addEventListener("pointerdown", (ev) => {
      const media = engine.info().mediaElement;
      if (!media || !Number.isFinite(media.duration) || media.duration <= 0) return;
      const target = ev.target as Element;
      if (target === refs.clipHandleStart) clipDragMode = "start";
      else if (target === refs.clipHandleEnd) clipDragMode = "end";
      else if (target === refs.clipSelection) clipDragMode = "window";
      else clipDragMode = "scrub";

      clipDragAnchorSec = trackPointerToTime(ev.clientX);
      clipDragStartBase = clipStartSec;
      clipDragEndBase = clipEndSec;

      if (clipDragMode === "scrub") {
        media.currentTime = clipDragAnchorSec;
        refs.clipPlayhead.value = String(clipDragAnchorSec);
      }
      syncClipUi();
      try {
        refs.clipTrack.setPointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
    });

    refs.clipTrack.addEventListener("pointermove", (ev) => {
      if (!clipDragMode) return;
      const media = engine.info().mediaElement;
      if (!media || !Number.isFinite(media.duration) || media.duration <= 0) return;
      const duration = media.duration;
      const t = trackPointerToTime(ev.clientX);

      if (clipDragMode === "start") {
        clipStartSec = Math.max(0, Math.min(t, clipEndSec - 0.1));
      } else if (clipDragMode === "end") {
        clipEndSec = Math.min(duration, Math.max(t, clipStartSec + 0.1));
      } else if (clipDragMode === "window") {
        const span = Math.max(0.1, clipDragEndBase - clipDragStartBase);
        const delta = t - clipDragAnchorSec;
        const nextStart = Math.max(0, Math.min(duration - span, clipDragStartBase + delta));
        clipStartSec = nextStart;
        clipEndSec = nextStart + span;
      } else if (clipDragMode === "scrub") {
        media.currentTime = t;
      }
      syncClipUi();
    });

    const stopDrag = (ev: PointerEvent): void => {
      if (!clipDragMode) return;
      clipDragMode = null;
      try {
        refs.clipTrack.releasePointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
    };
    refs.clipTrack.addEventListener("pointerup", stopDrag);
    refs.clipTrack.addEventListener("pointercancel", stopDrag);
  };

  const resetWaveformPreview = (): void => {
    clipWavePeaks = null;
    drawWaveform(null);
  };

  // ---- Mobile-friendly source UX ----
  // Hide tab/system audio on platforms that can't share it (iOS Safari/Chrome,
  // most Android browsers). Show a hint explaining the realistic phone path:
  // mic source or local audio file.
  const ua = navigator.userAgent || "";
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Mac") && navigator.maxTouchPoints > 1);
  const supportsTabAudio = Boolean(
    (navigator.mediaDevices as MediaDevices & {
      getDisplayMedia?: unknown;
    })?.getDisplayMedia,
  ) && !isIOS;
  if (!supportsTabAudio) {
    refs.sourceTabBtn.hidden = true;
  }
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/.test(ua);
  if (isMobile || !supportsTabAudio) {
    refs.mobileHint.hidden = false;
  }

  // ---- Input device picker ----
  // Browsers only return device labels after the user has granted mic
  // permission once, so the dropdown will start empty / generic and fill in
  // after the first mic capture succeeds.
  const refreshInputDevices = async (): Promise<void> => {
    try {
      const devices = await engine.enumerateInputs();
      const select = refs.inputDevice;
      const current = select.value;
      // Preserve the "System default" option, then append everything else.
      select.innerHTML = "";
      const defaultOpt = document.createElement("option");
      defaultOpt.value = "";
      defaultOpt.textContent = "System default";
      select.appendChild(defaultOpt);
      for (const dev of devices) {
        const opt = document.createElement("option");
        opt.value = dev.deviceId;
        opt.textContent = dev.label || `Mic (${dev.deviceId.slice(0, 6)})`;
        select.appendChild(opt);
      }
      // Restore prior selection if still available.
      if (current && [...select.options].some((o) => o.value === current)) {
        select.value = current;
      }
    } catch {
      /* enumeration unsupported */
    }
  };
  // Some browsers fire `devicechange` when a Bluetooth headset connects.
  navigator.mediaDevices?.addEventListener?.(
    "devicechange",
    () => void refreshInputDevices(),
  );
  void refreshInputDevices();

  refs.inputDevice.addEventListener("change", async () => {
    try {
      const info = await engine.useInput(refs.inputDevice.value || undefined);
      setStatus(`Source: ${info.label}`);
      void refreshInputDevices();
      resetWaveformPreview();
      refreshSourceDependentUi();
    } catch (err) {
      setStatus(
        `Failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  });

  document
    .querySelectorAll<HTMLButtonElement>("button[data-source]")
    .forEach((btn) => {
      btn.addEventListener("click", async () => {
        const kind = btn.dataset.source;
        try {
          if (kind === "mic") {
            const info = await engine.useInput(
              refs.inputDevice.value || undefined,
            );
            setStatus(`Source: ${info.label}`);
            void refreshInputDevices();
          } else if (kind === "tab") {
            const info = await engine.useTabAudio();
            setStatus(`Source: ${info.label}`);
          }
          resetWaveformPreview();
          refreshSourceDependentUi();
        } catch (err) {
          setStatus(
            `Failed: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      });
    });

  // ---- Tap-to-play (iOS autoplay fallback) ----
  // After loading an audio file, iOS Safari often refuses to start playback
  // until the user taps something. The "Tap to play" button only appears
  // when we detect the audio is paused after a fresh load.
  const refreshPlayButton = (): void => {
    const el = engine.info().mediaElement;
    if (!el) {
      refs.playPauseAudio.hidden = true;
      return;
    }
    refs.playPauseAudio.hidden = false;
    refs.playPauseAudio.textContent = el.paused ? "▶︎ Tap to play" : "⏸ Pause";
  };
  refs.playPauseAudio.addEventListener("click", () => {
    const el = engine.info().mediaElement;
    if (!el) return;
    if (el.paused) {
      void el.play().catch(() => {
        /* still blocked */
      });
    } else {
      el.pause();
    }
    setTimeout(refreshPlayButton, 60);
  });
  const monitorClipBoundary = (): void => {
    const el = engine.info().mediaElement;
    if (!el) return;
    if (refs.clipEnabled.checked && clipEndSec > clipStartSec) {
      if (el.currentTime >= clipEndSec) {
        if (clipPreviewing) {
          el.pause();
          el.currentTime = clipStartSec;
          clipPreviewing = false;
        } else {
          el.pause();
          el.currentTime = clipEndSec;
        }
      }
    }
  };
  setInterval(() => {
    refreshPlayButton();
    monitorClipBoundary();
    const el = engine.info().mediaElement;
    if (!el || !Number.isFinite(el.duration) || el.duration <= 0) return;
    refs.clipPlayhead.value = String(
      Math.max(0, Math.min(el.currentTime, el.duration)),
    );
    syncClipTrackVisuals();
  }, 120);

  refs.fileInput.addEventListener("change", () => {
    const f = refs.fileInput.files?.[0];
    if (!f) return;
    try {
      if (pendingAnalysis) {
        const info = engine.usePreanalyzed(f, pendingAnalysis);
        setStatus(`Source: ${info.label}`);
        pendingAnalysis = null;
      } else {
        const info = engine.useFile(f);
        setStatus(`Source: ${info.label}`);
      }
      void extractWavePeaksFromFile(f).then((peaks) => {
        clipWavePeaks = peaks;
        drawWaveform(clipWavePeaks);
      });
      applyLoadedMedia();
      // Auto-fill the overlay from ID3 tags if the user hasn't typed
      // anything yet — never clobber a user-edited title/artist/album.
      void NowPlayingOverlay.readMetadataFromFile(f).then((meta) => {
        let changed = false;
        if (!overlayCfg.title) {
          overlayCfg.title = meta.title;
          changed = true;
        }
        if (!overlayCfg.artist && meta.artist) {
          overlayCfg.artist = meta.artist;
          changed = true;
        }
        if (!overlayCfg.album && meta.album) {
          overlayCfg.album = meta.album;
          changed = true;
        }
        if (changed) {
          writeInputs();
          pushConfig();
        }
      });
    } catch (err) {
      setStatus(`Failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      refs.fileInput.value = "";
    }
  });

  refs.analysisInput.addEventListener("change", async () => {
    const f = refs.analysisInput.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      const data = JSON.parse(text) as AnalysisJson;
      if (!Array.isArray(data.beats) || !Array.isArray(data.energy_times)) {
        throw new Error("Not a valid analysis JSON.");
      }
      pendingAnalysis = data;
      setStatus(
        `Analysis loaded (${data.beats.length} beats). Now load the matching audio file.`,
      );
    } catch (err) {
      setStatus(
        `Bad analysis JSON: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      refs.analysisInput.value = "";
    }
  });

  refs.mediaUrlLoad.addEventListener("click", async () => {
    const raw = refs.mediaUrlInput.value.trim();
    if (!raw) {
      setStatus("Paste a media URL first.");
      return;
    }
    let parsed: URL;
    try {
      parsed = new URL(raw);
    } catch {
      setStatus("That is not a valid URL.");
      return;
    }

    const url = parsed.toString();
    const candidates = normalizeSharedMediaUrls(parsed);
    if (!isLikelyAudioUrl(url) && candidates.length === 0) {
      setStatus(
        "URL loaded rejected: provide a direct audio media URL (for example .mp3/.wav/.ogg).",
      );
      return;
    }

    refs.mediaUrlLoad.disabled = true;
    setStatus("Trying media URL...");
    for (const candidate of candidates) {
      const ok = await tryLoadRemoteCandidate(candidate);
      if (ok) {
        resetWaveformPreview();
        applyLoadedMedia();
        setStatus(
          `Source: URL: ${urlHostLabel(candidate)}. If levels stay flat, that host blocks cross-origin audio analysis.`,
        );
        refs.mediaUrlLoad.disabled = false;
        return;
      }
    }

    if (isKnownUnsupportedMediaPage(url)) {
      setStatus(
        "That appears to be a YouTube/Spotify/Bandcamp page link. The browser cannot extract protected stream audio directly here. Use a direct audio file/share link (mp3/wav/ogg), or download then load the file.",
      );
    } else {
      setStatus(
        "Could not load audio from that URL. Try a direct file URL or a public Dropbox/Google Drive file link.",
      );
    }
    refs.mediaUrlLoad.disabled = false;
  });
  refs.mediaUrlInput.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      refs.mediaUrlLoad.click();
    }
  });

  refs.clipPlayhead.addEventListener("input", () => {
    const el = engine.info().mediaElement;
    if (!el) return;
    const t = Number(refs.clipPlayhead.value);
    if (!Number.isFinite(t)) return;
    el.currentTime = t;
    syncClipUi();
  });

  refs.clipStart.addEventListener("input", () => {
    const el = engine.info().mediaElement;
    if (!el || !Number.isFinite(el.duration) || el.duration <= 0) return;
    clipStartSec = Math.max(0, Math.min(Number(refs.clipStart.value), el.duration));
    if (clipStartSec >= clipEndSec) {
      clipEndSec = Math.min(el.duration, clipStartSec + 0.1);
    }
    syncClipUi();
  });

  refs.clipEnd.addEventListener("input", () => {
    const el = engine.info().mediaElement;
    if (!el || !Number.isFinite(el.duration) || el.duration <= 0) return;
    clipEndSec = Math.max(0, Math.min(Number(refs.clipEnd.value), el.duration));
    if (clipEndSec <= clipStartSec) {
      clipStartSec = Math.max(0, clipEndSec - 0.1);
    }
    syncClipUi();
  });

  refs.clipSetStart.addEventListener("click", () => {
    const el = engine.info().mediaElement;
    if (!el || !Number.isFinite(el.duration) || el.duration <= 0) return;
    clipStartSec = Math.max(0, Math.min(el.currentTime, clipEndSec - 0.1));
    syncClipUi();
  });

  refs.clipSetEnd.addEventListener("click", () => {
    const el = engine.info().mediaElement;
    if (!el || !Number.isFinite(el.duration) || el.duration <= 0) return;
    clipEndSec = Math.max(clipStartSec + 0.1, Math.min(el.currentTime, el.duration));
    syncClipUi();
  });

  refs.clipPreview.addEventListener("click", () => {
    const el = engine.info().mediaElement;
    if (!el || !refs.clipEnabled.checked) return;
    clipPreviewing = true;
    el.currentTime = clipStartSec;
    void el.play().catch(() => {
      clipPreviewing = false;
    });
  });

  refs.clipEnabled.addEventListener("change", () => {
    clipPreviewing = false;
    syncClipUi();
  });
  attachClipTrackPointerUi();
  drawWaveform(null);
  syncClipUi();

  // -------- Look & Mood panel --------
  const lookPanel = buildLookPanel(refs.lookPanel, renderer);
  const refreshCameraFramingPanel = setupCameraFramingPanel(
    renderer,
    () => lookPanel.refresh(),
  );

  // -------- Now Playing overlay --------
  // The overlay lives inside the aspect frame so it sits over the canvas
  // for live display. Recording uses RecordingCompositor (created below
  // alongside the image overlay) to bake the same overlay into the saved
  // file via Canvas2D — `canvas.captureStream()` cannot see DOM layers.
  // Config persists in localStorage, and the lyric ticker reads
  // currentTime from whichever <audio> element the engine is using.
  const OVERLAY_KEY = "pulsefx.overlay";
  const overlay = new NowPlayingOverlay(refs.frame);
  let overlayCfg: OverlayConfig = loadOverlayConfig() ?? { ...DEFAULT_OVERLAY };

  const writeInputs = (): void => {
    refs.overlay.enabled.checked = overlayCfg.enabled;
    refs.overlay.title.value = overlayCfg.title;
    refs.overlay.artist.value = overlayCfg.artist;
    refs.overlay.album.value = overlayCfg.album;
    refs.overlay.position.value = overlayCfg.position;
    refs.overlay.showLyrics.checked = overlayCfg.showLyrics;
    refs.overlay.lyricMode.value = overlayCfg.lyricMode;
    refs.overlay.lyrics.value = overlayCfg.lyrics;
    refs.overlay.font.value = overlayCfg.fontFamily;
    refs.overlay.weight.value = overlayCfg.fontWeight;
    refs.overlay.uppercase.checked = overlayCfg.uppercase;
    refs.overlay.watermarkEnabled.checked = overlayCfg.watermarkEnabled;
    refs.overlay.watermarkHandle.value = overlayCfg.watermarkHandle;
    refs.overlay.watermarkPosition.value = overlayCfg.watermarkPosition;
    refs.overlay.artLabel.textContent = overlayCfg.albumArt
      ? "Replace image…"
      : "Load image…";
    refs.overlay.watermarkLogoLabel.textContent = overlayCfg.watermarkLogo
      ? "Replace logo…"
      : "Load logo…";
  };
  const pushConfig = (): void => {
    overlay.setConfig(overlayCfg);
    try {
      localStorage.setItem(OVERLAY_KEY, JSON.stringify(overlayCfg));
    } catch {
      /* private mode etc. */
    }
  };
  writeInputs();
  pushConfig();

  refs.overlay.enabled.addEventListener("change", () => {
    overlayCfg.enabled = refs.overlay.enabled.checked;
    pushConfig();
  });
  refs.overlay.title.addEventListener("input", () => {
    overlayCfg.title = refs.overlay.title.value;
    pushConfig();
  });
  refs.overlay.artist.addEventListener("input", () => {
    overlayCfg.artist = refs.overlay.artist.value;
    pushConfig();
  });
  refs.overlay.album.addEventListener("input", () => {
    overlayCfg.album = refs.overlay.album.value;
    pushConfig();
  });
  refs.overlay.position.addEventListener("change", () => {
    overlayCfg.position = refs.overlay.position.value as OverlayPosition;
    pushConfig();
  });
  refs.overlay.showLyrics.addEventListener("change", () => {
    overlayCfg.showLyrics = refs.overlay.showLyrics.checked;
    pushConfig();
  });
  refs.overlay.lyrics.addEventListener("input", () => {
    overlayCfg.lyrics = refs.overlay.lyrics.value;
    pushConfig();
  });
  refs.overlay.lyricMode.addEventListener("change", () => {
    overlayCfg.lyricMode = refs.overlay.lyricMode.value as LyricMode;
    pushConfig();
  });
  refs.overlay.font.addEventListener("change", () => {
    overlayCfg.fontFamily = refs.overlay.font.value as FontFamily;
    pushConfig();
  });
  refs.overlay.weight.addEventListener("change", () => {
    overlayCfg.fontWeight = refs.overlay.weight.value as FontWeight;
    pushConfig();
  });
  refs.overlay.uppercase.addEventListener("change", () => {
    overlayCfg.uppercase = refs.overlay.uppercase.checked;
    pushConfig();
  });
  refs.overlay.watermarkEnabled.addEventListener("change", () => {
    overlayCfg.watermarkEnabled = refs.overlay.watermarkEnabled.checked;
    pushConfig();
  });
  refs.overlay.watermarkHandle.addEventListener("input", () => {
    overlayCfg.watermarkHandle = refs.overlay.watermarkHandle.value;
    pushConfig();
  });
  refs.overlay.watermarkPosition.addEventListener("change", () => {
    overlayCfg.watermarkPosition = refs.overlay.watermarkPosition
      .value as OverlayPosition;
    pushConfig();
  });

  // ---- Image file -> data URL helpers ----
  const loadImageAsDataUrl = async (file: File): Promise<string> => {
    if (!file.type.startsWith("image/")) {
      throw new Error("Not an image file.");
    }
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error ?? new Error("read error"));
      reader.readAsDataURL(file);
    });
  };

  refs.overlay.artFile.addEventListener("change", async () => {
    const f = refs.overlay.artFile.files?.[0];
    if (!f) return;
    try {
      overlayCfg.albumArt = await loadImageAsDataUrl(f);
      writeInputs();
      pushConfig();
    } catch (err) {
      setStatus(
        `Album art failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      refs.overlay.artFile.value = "";
    }
  });
  refs.overlay.artClear.addEventListener("click", () => {
    overlayCfg.albumArt = "";
    writeInputs();
    pushConfig();
  });

  refs.overlay.watermarkLogoFile.addEventListener("change", async () => {
    const f = refs.overlay.watermarkLogoFile.files?.[0];
    if (!f) return;
    try {
      overlayCfg.watermarkLogo = await loadImageAsDataUrl(f);
      writeInputs();
      pushConfig();
    } catch (err) {
      setStatus(
        `Logo failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      refs.overlay.watermarkLogoFile.value = "";
    }
  });
  refs.overlay.watermarkLogoClear.addEventListener("click", () => {
    overlayCfg.watermarkLogo = "";
    writeInputs();
    pushConfig();
  });
  refs.overlay.lyricsFile.addEventListener("change", async () => {
    const f = refs.overlay.lyricsFile.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      // If it's JSON ([{ t, line }] or { lyrics: "..." }), accept it.
      let parsed = text;
      try {
        const data = JSON.parse(text);
        if (typeof data === "string") parsed = data;
        else if (typeof data?.lyrics === "string") parsed = data.lyrics;
        else if (Array.isArray(data)) {
          parsed = (data as { t?: number; line?: string }[])
            .map((row) => {
              if (typeof row.t !== "number" || typeof row.line !== "string") {
                return "";
              }
              const min = Math.floor(row.t / 60);
              const sec = (row.t - min * 60).toFixed(2).padStart(5, "0");
              return `[${String(min).padStart(2, "0")}:${sec}] ${row.line}`;
            })
            .filter(Boolean)
            .join("\n");
        }
      } catch {
        /* not JSON, treat as raw text */
      }
      overlayCfg.lyrics = parsed;
      writeInputs();
      pushConfig();
    } finally {
      refs.overlay.lyricsFile.value = "";
    }
  });

  // Tick the overlay (and try to auto-fill metadata when a new file is loaded).
  const overlayLoop = (): void => {
    const info = engine.info();
    const el = info.mediaElement ?? null;
    overlay.tick(el ? el.currentTime : null);
    requestAnimationFrame(overlayLoop);
  };
  requestAnimationFrame(overlayLoop);

  // -------- Brand Kit (Videobolt-style reusable branding) --------
  // One saved bundle of overlay branding fields: logo, handle, position,
  // font, weight, UPPERCASE. "Save" snapshots the current overlay; "Apply"
  // writes the saved values back so the user can drop their identity onto
  // any clip/preset with one click.
  const BRAND_KIT_KEY = "pulsefx.brandKit.v1";
  interface BrandKit {
    watermarkLogo: string;
    watermarkHandle: string;
    watermarkPosition: OverlayPosition;
    fontFamily: FontFamily;
    fontWeight: FontWeight;
    uppercase: boolean;
  }
  let brandKitMsgTimer: number | null = null;
  const setBrandKitStatus = (msg: string): void => {
    refs.brandKitStatus.textContent = msg;
    if (brandKitMsgTimer !== null) window.clearTimeout(brandKitMsgTimer);
    if (!msg) return;
    brandKitMsgTimer = window.setTimeout(() => {
      refs.brandKitStatus.textContent = "";
      brandKitMsgTimer = null;
    }, 3000);
  };
  const loadBrandKit = (): BrandKit | null => {
    try {
      const raw = localStorage.getItem(BRAND_KIT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<BrandKit>;
      return {
        watermarkLogo: typeof parsed.watermarkLogo === "string" ? parsed.watermarkLogo : "",
        watermarkHandle: typeof parsed.watermarkHandle === "string" ? parsed.watermarkHandle : "",
        watermarkPosition: (parsed.watermarkPosition as OverlayPosition) ?? "bottom-right",
        fontFamily: (parsed.fontFamily as FontFamily) ?? "sans",
        fontWeight: (parsed.fontWeight as FontWeight) ?? "bold",
        uppercase: !!parsed.uppercase,
      };
    } catch {
      return null;
    }
  };
  refs.brandKitApply.disabled = loadBrandKit() === null;
  refs.brandKitSave.addEventListener("click", () => {
    const kit: BrandKit = {
      watermarkLogo: overlayCfg.watermarkLogo,
      watermarkHandle: overlayCfg.watermarkHandle,
      watermarkPosition: overlayCfg.watermarkPosition,
      fontFamily: overlayCfg.fontFamily,
      fontWeight: overlayCfg.fontWeight,
      uppercase: overlayCfg.uppercase,
    };
    try {
      localStorage.setItem(BRAND_KIT_KEY, JSON.stringify(kit));
      refs.brandKitApply.disabled = false;
      setBrandKitStatus("Brand kit saved.");
    } catch {
      setBrandKitStatus("Save failed (storage full?).");
    }
  });
  refs.brandKitApply.addEventListener("click", () => {
    const kit = loadBrandKit();
    if (!kit) {
      setBrandKitStatus("No brand kit saved yet.");
      return;
    }
    overlayCfg.watermarkLogo = kit.watermarkLogo;
    overlayCfg.watermarkHandle = kit.watermarkHandle;
    overlayCfg.watermarkPosition = kit.watermarkPosition;
    overlayCfg.fontFamily = kit.fontFamily;
    overlayCfg.fontWeight = kit.fontWeight;
    overlayCfg.uppercase = kit.uppercase;
    // Switch the watermark on so users see the brand land.
    overlayCfg.watermarkEnabled = true;
    overlayCfg.enabled = true;
    writeInputs();
    pushConfig();
    setBrandKitStatus("Applied.");
  });

  // -------- Randomize button (AudioSpace-style one-click variation) --------
  // Re-rolls the look knobs within musically usable ranges. Picks a random
  // concept-mode roughly half the time so manual users see meaningful
  // structural variety, not just a hue spin. Layer toggles are *not*
  // touched here — the active scene's layer mix stays intact so randomize
  // feels like "new mood" rather than "different scene".
  const rand = (lo: number, hi: number): number => lo + Math.random() * (hi - lo);
  refs.randomizeLook.addEventListener("click", () => {
    const p = renderer.params;
    p.paletteHue = Math.floor(rand(0, 360));
    p.saturation = rand(0.55, 1.0);
    p.intensity = rand(0.85, 1.6);
    p.starSize = rand(0.12, 0.28);
    p.bassGravity = rand(0.7, 1.7);
    p.midCurl = rand(0.6, 1.5);
    p.trebleShimmer = rand(0.5, 1.3);
    p.cymaticStrength = rand(0.9, 1.9);
    p.flowSpeed = rand(0.6, 1.4);
    p.atmosphere = rand(0.4, 0.9);
    if (Math.random() < 0.5) {
      const choices = CONCEPT_MODES.filter((c) => c.value !== "none");
      p.conceptMode = choices[Math.floor(Math.random() * choices.length)].value;
    } else {
      p.conceptMode = "none";
    }
    lookPanel.refresh();
  });

  // -------- Auto-switch preset on silence (AudioSpace pattern) --------
  // When level stays below SILENCE_LEVEL for SILENCE_HOLD_MS and then audio
  // returns, pick a random saved preset (different from current) and apply.
  // Persists the toggle so users opt in once and forget.
  const AUTO_SWITCH_KEY = "pulsefx.autoSwitchSilence";
  const SILENCE_LEVEL = 0.025;
  const SILENCE_HOLD_MS = 1500;
  let autoSwitchEnabled = localStorage.getItem(AUTO_SWITCH_KEY) === "1";
  refs.autoSwitchSilence.checked = autoSwitchEnabled;
  refs.autoSwitchSilence.addEventListener("change", () => {
    autoSwitchEnabled = refs.autoSwitchSilence.checked;
    try {
      localStorage.setItem(AUTO_SWITCH_KEY, autoSwitchEnabled ? "1" : "0");
    } catch {
      /* ignore */
    }
  });
  let silenceStart = 0;
  let lastSwitchAt = 0;
  let lastAppliedPreset = "";
  const silenceLoop = (): void => {
    if (autoSwitchEnabled) {
      const f = engine.read();
      const now = performance.now();
      const list = presets.list();
      if (f.level < SILENCE_LEVEL) {
        if (silenceStart === 0) silenceStart = now;
      } else if (silenceStart > 0) {
        const heldMs = now - silenceStart;
        silenceStart = 0;
        // Require enough silence + at least 2 presets + cooldown so we don't
        // ping-pong on micro-pauses or mid-track quiet bars.
        if (
          heldMs >= SILENCE_HOLD_MS &&
          list.length >= 2 &&
          now - lastSwitchAt > 4000
        ) {
          const candidates = list.filter((p) => p.name !== lastAppliedPreset);
          const pick = candidates[Math.floor(Math.random() * candidates.length)];
          if (pick) {
            Object.assign(renderer.params, pick.params);
            lookPanel.refresh();
            lastAppliedPreset = pick.name;
            lastSwitchAt = now;
          }
        }
      }
    }
    requestAnimationFrame(silenceLoop);
  };
  requestAnimationFrame(silenceLoop);

  // -------- Post-FX stack (vizz.fm-style chained filters) --------
  // Each effect is a 0..1 strength slider. Builds the row UI dynamically so
  // we don't bloat the HTML; the spec list below is the single source of
  // truth for ordering, labels, and step sizes.
  interface FxSpec {
    key: keyof BioclastParams;
    label: string;
    max?: number;
    step?: number;
  }
  const FX_SPECS: FxSpec[] = [
    { key: "fxKaleidoscope", label: "Kaleidoscope" },
    { key: "fxKaleidoscopeSegments", label: "Kaleido segs", max: 32, step: 1 },
    { key: "fxSwirl", label: "Swirl" },
    { key: "fxBarrel", label: "Barrel/Pinch", max: 1, step: 0.02 },
    { key: "fxGridTile", label: "Grid Tile" },
    { key: "fxGridTileColumns", label: "Tile cols", max: 8, step: 1 },
    { key: "fxPixelate", label: "Pixelate" },
    { key: "fxGlitch", label: "Glitch" },
    { key: "fxRadialBlur", label: "Radial Blur" },
    { key: "fxRgbShift", label: "RGB Shift" },
    { key: "fxDotScreen", label: "Dot Screen" },
    { key: "fxDither", label: "Dither" },
    { key: "fxCrt", label: "CRT" },
    { key: "fxTiltShift", label: "Tilt-Shift" },
    { key: "fxVignette", label: "Vignette" },
    { key: "fxFilmGrain", label: "Film Grain" },
  ];
  const CAM_SPECS: FxSpec[] = [
    { key: "camAutoRotateH", label: "Auto-Rotate H", max: 1.0, step: 0.005 },
    { key: "camAutoRotateV", label: "Auto-Rotate V", max: 0.5, step: 0.005 },
    { key: "camAutoRoll", label: "Auto-Roll", max: 0.5, step: 0.005 },
    { key: "camZoomAmount", label: "Zoom Amount", max: 2.0, step: 0.05 },
    { key: "camZoomPeriod", label: "Zoom Period (s)", max: 30, step: 0.5 },
    { key: "camFovAmount", label: "FOV Amount (°)", max: 30, step: 0.5 },
    { key: "camFovPeriod", label: "FOV Period (s)", max: 30, step: 0.5 },
    { key: "camPanAmount", label: "Pan Amount", max: 5, step: 0.1 },
    { key: "camPanPeriod", label: "Pan Period (s)", max: 30, step: 0.5 },
  ];
  const buildSliderRow = (spec: FxSpec, container: HTMLElement): void => {
    const max = spec.max ?? 1;
    const step = spec.step ?? 0.01;
    const row = document.createElement("div");
    row.className = "postFxRow";
    const label = document.createElement("div");
    label.className = "postFxRow__label";
    label.textContent = spec.label;
    const input = document.createElement("input");
    input.type = "range";
    input.min = "0";
    input.max = String(max);
    input.step = String(step);
    const val = document.createElement("div");
    val.className = "postFxRow__val";
    const sync = (): void => {
      const v = Number(renderer.params[spec.key as keyof BioclastParams]);
      input.value = String(v);
      val.textContent =
        max <= 1 ? v.toFixed(2) : v.toFixed(step >= 1 ? 0 : 2);
    };
    input.addEventListener("input", () => {
      const v = Number(input.value);
      // Cast through unknown so TS lets us write to a numeric BioclastParams key.
      (renderer.params as unknown as Record<string, number>)[spec.key as string] = v;
      val.textContent =
        max <= 1 ? v.toFixed(2) : v.toFixed(step >= 1 ? 0 : 2);
    });
    sync();
    row.appendChild(label);
    row.appendChild(input);
    row.appendChild(val);
    container.appendChild(row);
    // Re-sync when external code (preset load, randomize) mutates params.
    fxRefreshers.push(sync);
  };
  const fxRefreshers: Array<() => void> = [];
  for (const s of FX_SPECS) buildSliderRow(s, refs.postFxSliders);
  for (const s of CAM_SPECS) buildSliderRow(s, refs.cameraMotionSliders);

  refs.postFxEnabled.checked = renderer.params.postFxEnabled;
  refs.postFxEnabled.addEventListener("change", () => {
    renderer.params.postFxEnabled = refs.postFxEnabled.checked;
  });
  refs.postFxClearAll.addEventListener("click", () => {
    for (const s of FX_SPECS) {
      (renderer.params as unknown as Record<string, number>)[s.key as string] = 0;
    }
    for (const r of fxRefreshers) r();
  });
  refs.cameraMotionReset.addEventListener("click", () => {
    const p = renderer.params;
    p.camAutoRotateH = 0;
    p.camAutoRotateV = 0;
    p.camAutoRoll = 0;
    p.camZoomAmount = 0;
    p.camFovAmount = 0;
    p.camPanAmount = 0;
    for (const r of fxRefreshers) r();
  });

  // -------- Background style (vizz.fm-style backdrop) --------
  // Persists style/colors/reactivity to localStorage so the user's pick
  // survives reloads independent of the saved-presets system.
  const BG_KEY = "pulsefx.background.v1";
  interface BgPersist {
    style: BgStyle;
    primary: string;
    secondary: string;
    reactivity: number;
  }
  const loadBg = (): Partial<BgPersist> => {
    try {
      const raw = localStorage.getItem(BG_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as Partial<BgPersist>;
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  };
  const saveBg = (cfg: BgPersist): void => {
    try {
      localStorage.setItem(BG_KEY, JSON.stringify(cfg));
    } catch {
      /* private mode */
    }
  };
  if (background) {
    const stored = loadBg();
    const initial: BgPersist = {
      style: (stored.style as BgStyle) ?? "tint",
      primary: typeof stored.primary === "string" ? stored.primary : "#06122a",
      secondary: typeof stored.secondary === "string" ? stored.secondary : "#000000",
      reactivity: typeof stored.reactivity === "number" ? stored.reactivity : 1,
    };
    refs.bgStyle.value = initial.style;
    refs.bgPrimary.value = initial.primary;
    refs.bgSecondary.value = initial.secondary;
    refs.bgReactivity.value = String(initial.reactivity);
    background.setConfig(initial);

    const pushBg = (): void => {
      const cfg: BgPersist = {
        style: refs.bgStyle.value as BgStyle,
        primary: refs.bgPrimary.value,
        secondary: refs.bgSecondary.value,
        reactivity: Number(refs.bgReactivity.value),
      };
      background.setConfig(cfg);
      saveBg(cfg);
    };
    refs.bgStyle.addEventListener("change", pushBg);
    refs.bgPrimary.addEventListener("input", pushBg);
    refs.bgSecondary.addEventListener("input", pushBg);
    refs.bgReactivity.addEventListener("input", pushBg);
  } else {
    // No backdrop element on this page (e.g. milkdrop entry) \u2014 disable.
    refs.bgStyle.disabled = true;
    refs.bgPrimary.disabled = true;
    refs.bgSecondary.disabled = true;
    refs.bgReactivity.disabled = true;
  }

  // Re-sync the dynamic sliders whenever a preset / share-link / randomize
  // mutates params — piggyback on the existing lookPanel.refresh by wrapping it.
  const originalRefresh = lookPanel.refresh.bind(lookPanel);
  lookPanel.refresh = (): void => {
    originalRefresh();
    for (const r of fxRefreshers) r();
    refreshCameraFramingPanel();
  };

  // -------- Visualizer picker (scene type switch) --------
  // Tile grid \u2014 click to switch between Bioclast (legacy 16-layer) and the
  // four vizz.fm-style alt scenes. Active tile gets a glow border.
  const SCENE_KEY = "pulsefx.sceneType.v1";
  const sceneOrder: { type: SceneType; label: string }[] = [
    { type: "bioclast", label: "Bioclast" },
    { type: "meshGrid", label: ALT_SCENE_LABELS.meshGrid },
    { type: "wavyForm", label: ALT_SCENE_LABELS.wavyForm },
    { type: "texturedSphere", label: ALT_SCENE_LABELS.texturedSphere },
    { type: "galaxy", label: ALT_SCENE_LABELS.galaxy },
    { type: "dotGrid", label: ALT_SCENE_LABELS.dotGrid },
    { type: "frequencyBars", label: ALT_SCENE_LABELS.frequencyBars },
  ];
  const setActiveSceneTile = (type: SceneType): void => {
    refs.visualizerGrid
      .querySelectorAll<HTMLElement>(".visualizerGrid__tile")
      .forEach((tile) => {
        tile.classList.toggle("is-active", tile.dataset.scene === type);
      });
  };
  for (const { type, label } of sceneOrder) {
    const tile = document.createElement("button");
    tile.type = "button";
    tile.className = "visualizerGrid__tile";
    tile.dataset.scene = type;
    const lab = document.createElement("span");
    lab.className = "visualizerGrid__label";
    lab.textContent = label;
    tile.appendChild(lab);
    tile.addEventListener("click", () => {
      renderer.sceneType = type;
      setActiveSceneTile(type);
      updateSceneStatus(type);
      try { localStorage.setItem(SCENE_KEY, type); } catch { /* ignore */ }
    });
    refs.visualizerGrid.appendChild(tile);
  }
  // Restore last scene type.
  try {
    const saved = localStorage.getItem(SCENE_KEY);
    if (saved && sceneOrder.some((s) => s.type === saved)) {
      renderer.sceneType = saved as SceneType;
    }
  } catch { /* ignore */ }
  setActiveSceneTile(renderer.sceneType);
  updateSceneStatus(renderer.sceneType);

  // -------- Modulations (vizz.fm `controlModulations`) --------
  // Build a fixed list of modulatable params \u2014 every numeric BioclastParams
  // key is fair game. We expose the most useful subset to keep the dropdown
  // browsable; users can extend by editing the constant.
  const MODULATABLE_PARAMS = [
    "paletteHue", "intensity", "saturation", "starSize", "atmosphere",
    "bassGravity", "midCurl", "trebleShimmer", "cymaticStrength", "flowSpeed",
    "cameraSpin", "cameraZoom", "cameraTilt", "cameraOrbit",
    "bloomStrength", "bloomRadius", "feedbackStrength", "feedbackZoom",
    "fxKaleidoscope", "fxSwirl", "fxGlitch", "fxRgbShift", "fxRadialBlur",
    "fxBarrel", "fxGridTile", "fxPixelate", "fxCrt", "fxTiltShift",
    "fxVignette", "fxFilmGrain", "fxDither", "fxDotScreen",
  ] as const;
  for (const k of MODULATABLE_PARAMS) {
    const opt = document.createElement("option");
    opt.value = k;
    opt.textContent = k;
    refs.modParam.appendChild(opt);
  }
  const MOD_KEY = "pulsefx.modulations.v1";
  const loadMods = (): Record<string, Modulation> => {
    try {
      const raw = localStorage.getItem(MOD_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch { return {}; }
  };
  const saveMods = (): void => {
    try { localStorage.setItem(MOD_KEY, JSON.stringify(renderer.modulations)); } catch { /* ignore */ }
  };
  // Hydrate.
  Object.assign(renderer.modulations, loadMods());
  const renderModList = (): void => {
    refs.modList.innerHTML = "";
    const mods = renderer.modulations as Record<string, Modulation | undefined>;
    for (const key of Object.keys(mods)) {
      const m = mods[key];
      if (!m) continue;
      const row = document.createElement("div");
      row.className = "modRow";
      const name = document.createElement("span");
      name.textContent = key;
      name.title = key;
      row.appendChild(name);
      const mode = document.createElement("select");
      for (const opt of ["audio", "oscillate"]) {
        const o = document.createElement("option");
        o.value = opt;
        o.textContent = opt;
        if (m.mode === opt) o.selected = true;
        mode.appendChild(o);
      }
      mode.addEventListener("change", () => {
        m.mode = mode.value as ModulationMode;
        saveMods();
      });
      row.appendChild(mode);
      const easing = document.createElement("select");
      for (const opt of ["linear", "sine", "ease-in", "ease-out", "ease-in-out"]) {
        const o = document.createElement("option");
        o.value = opt;
        o.textContent = opt;
        if (m.easing === opt) o.selected = true;
        easing.appendChild(o);
      }
      easing.addEventListener("change", () => {
        m.easing = easing.value as ModulationEasing;
        saveMods();
      });
      row.appendChild(easing);
      const min = document.createElement("input");
      min.type = "number"; min.step = "0.01"; min.value = String(m.min);
      min.addEventListener("change", () => { m.min = Number(min.value); saveMods(); });
      row.appendChild(min);
      const max = document.createElement("input");
      max.type = "number"; max.step = "0.01"; max.value = String(m.max);
      max.addEventListener("change", () => { m.max = Number(max.value); saveMods(); });
      row.appendChild(max);
      const del = document.createElement("button");
      del.type = "button";
      del.className = "modRow__del";
      del.textContent = "x";
      del.title = "Remove modulation";
      del.addEventListener("click", () => {
        delete (renderer.modulations as Record<string, unknown>)[key];
        saveMods();
        renderModList();
      });
      row.appendChild(del);
      refs.modList.appendChild(row);
    }
  };
  refs.modAdd.addEventListener("click", () => {
    const key = refs.modParam.value;
    if (!key) return;
    if ((renderer.modulations as Record<string, unknown>)[key]) {
      renderModList();
      return;
    }
    const cur = (renderer.params as unknown as Record<string, number>)[key];
    const base = typeof cur === "number" ? cur : 1;
    const m: Modulation = {
      ...DEFAULT_MODULATION,
      enabled: true,
      mode: refs.modMode.value as ModulationMode,
      min: Math.max(0, base * 0.5),
      max: Math.max(base * 1.5, base + 0.5),
      speed: refs.modMode.value === "oscillate" ? 0.25 : 1,
    };
    (renderer.modulations as Record<string, Modulation>)[key] = m;
    saveMods();
    renderModList();
  });
  renderModList();

  // -------- vizz.fm Preset Library (60 hand-translated presets) --------
  // One-tap apply: writes scene type + look + post-fx + background.
  const groups = Array.from(new Set(VIZZ_PRESETS.map((p) => p.group)));
  for (const g of groups) {
    const opt = document.createElement("option");
    opt.value = g;
    opt.textContent = g;
    refs.vizzLibraryGroup.appendChild(opt);
  }
  // Morph state — populated by applyPreset when duration > 0.
  const morphState = createMorphState();
  let morphSnapshot: BioclastParams = JSON.parse(JSON.stringify(renderer.params));
  // Persist morph + apply-include settings across reloads.
  const MORPH_KEY = "pulsefx.morph.v2";
  type MorphSettings = {
    duration: number; easing: string; replace: boolean;
    incLook: boolean; incScene: boolean; incCamera: boolean; incBg: boolean; incFx: boolean;
  };
  const defaultIncludes: MorphSettings = {
    duration: 0, easing: "ease-in-out", replace: false,
    incLook: true, incScene: false, incCamera: false, incBg: false, incFx: false,
  };
  try {
    const raw = localStorage.getItem(MORPH_KEY);
    if (raw) {
      const s = { ...defaultIncludes, ...JSON.parse(raw) } as MorphSettings;
      refs.morphDuration.value = String(s.duration);
      refs.morphEasing.value = s.easing;
      refs.vizzReplaceLook.checked = s.replace;
      refs.vizzApplyLook.checked = s.incLook;
      refs.vizzApplyScene.checked = s.incScene;
      refs.vizzApplyCamera.checked = s.incCamera;
      refs.vizzApplyBg.checked = s.incBg;
      refs.vizzApplyFx.checked = s.incFx;
    }
  } catch { /* ignore */ }
  const saveMorphSettings = (): void => {
    try {
      localStorage.setItem(MORPH_KEY, JSON.stringify({
        duration: parseFloat(refs.morphDuration.value) || 0,
        easing: refs.morphEasing.value,
        replace: refs.vizzReplaceLook.checked,
        incLook: refs.vizzApplyLook.checked,
        incScene: refs.vizzApplyScene.checked,
        incCamera: refs.vizzApplyCamera.checked,
        incBg: refs.vizzApplyBg.checked,
        incFx: refs.vizzApplyFx.checked,
      }));
    } catch { /* ignore */ }
  };
  for (const el of [refs.morphDuration, refs.morphEasing, refs.vizzReplaceLook, refs.vizzApplyLook, refs.vizzApplyScene, refs.vizzApplyCamera, refs.vizzApplyBg, refs.vizzApplyFx]) {
    el.addEventListener("change", saveMorphSettings);
  }
  // Segmented "Apply" picker: Look only | Look + Camera | Full recipe.
  // The three buttons are mutually exclusive presets that drive the
  // underlying include checkboxes; the granular checkboxes remain
  // available behind the Advanced disclosure so power users can deviate.
  const applyButtons: HTMLButtonElement[] = [
    refs.vizzApplyLookOnly,
    refs.vizzApplyLookCam,
    refs.vizzApplyAll,
  ];
  const setApplyMode = (active: HTMLButtonElement, opts: {
    look: boolean; scene: boolean; camera: boolean; bg: boolean; fx: boolean;
  }): void => {
    refs.vizzApplyLook.checked = opts.look;
    refs.vizzApplyScene.checked = opts.scene;
    refs.vizzApplyCamera.checked = opts.camera;
    refs.vizzApplyBg.checked = opts.bg;
    refs.vizzApplyFx.checked = opts.fx;
    applyButtons.forEach((b) => {
      const isOn = b === active;
      b.classList.toggle("is-active", isOn);
      b.setAttribute("aria-checked", isOn ? "true" : "false");
    });
    saveMorphSettings();
  };
  refs.vizzApplyAll.addEventListener("click", () => {
    setApplyMode(refs.vizzApplyAll, { look: true, scene: true, camera: true, bg: true, fx: true });
  });
  refs.vizzApplyLookOnly.addEventListener("click", () => {
    setApplyMode(refs.vizzApplyLookOnly, { look: true, scene: false, camera: false, bg: false, fx: false });
  });
  refs.vizzApplyLookCam.addEventListener("click", () => {
    setApplyMode(refs.vizzApplyLookCam, { look: true, scene: false, camera: true, bg: false, fx: false });
  });
  // When the user manually toggles checkboxes inside Advanced, drop the
  // segmented active state so the UI doesn't lie about the current mode.
  for (const cb of [refs.vizzApplyLook, refs.vizzApplyScene, refs.vizzApplyCamera, refs.vizzApplyBg, refs.vizzApplyFx]) {
    cb.addEventListener("change", () => {
      applyButtons.forEach((b) => {
        b.classList.remove("is-active");
        b.setAttribute("aria-checked", "false");
      });
    });
  }
  refs.vizzFreeCamera.addEventListener("click", () => {
    renderer.clearAltCameraOverride();
  });
  // Drive morph each frame.
  const morphLoop = (): void => {
    if (morphState.active) {
      tickMorph(morphState, renderer.params);
      lookPanel.refresh();
    }
    requestAnimationFrame(morphLoop);
  };
  requestAnimationFrame(morphLoop);
  refs.morphCancel.addEventListener("click", () => cancelMorph(morphState));
  const setBgFromPreset = (bg: Parameters<typeof applyVizPreset>[3] extends ((b: infer T) => void) | null ? T : never): void => {
    if (background) background.setConfig(bg);
    // Also push back into the BG controls so they reflect the change.
    if (bg.style) refs.bgStyle.value = bg.style;
    if (bg.primary) refs.bgPrimary.value = bg.primary;
    if (bg.secondary) refs.bgSecondary.value = bg.secondary;
    if (typeof bg.reactivity === "number") refs.bgReactivity.value = String(bg.reactivity);
  };
  const readAdvancedApplyOptions = (): ApplyOptions => ({
    mode: refs.vizzReplaceLook.checked ? "replace" : "layer",
    include: {
      look: refs.vizzApplyLook.checked,
      scene: refs.vizzApplyScene.checked,
      camera: refs.vizzApplyCamera.checked,
      bg: refs.vizzApplyBg.checked,
      fx: refs.vizzApplyFx.checked,
    },
  });
  const lookOnlyOptions = (): ApplyOptions => ({
    mode: "layer",
    include: { look: true, scene: false, camera: false, bg: false, fx: false },
  });
  const sceneLookOptions = (): ApplyOptions => ({
    mode: "layer",
    include: { look: true, scene: true, camera: false, bg: false, fx: false },
  });
  const fullRecipeOptions = (): ApplyOptions => ({
    mode: "layer",
    include: { look: true, scene: true, camera: true, bg: true, fx: true },
  });
  const applyPreset = (preset: VizPreset, overrideOptions?: ApplyOptions, statusText?: string): void => {
    const dur = parseFloat(refs.morphDuration.value) || 0;
    const opts = overrideOptions ?? readAdvancedApplyOptions();
    const setPresetScene = (t: SceneType): void => {
      renderer.sceneType = t;
      setActiveSceneTile(t);
      updateSceneStatus(t);
      try { localStorage.setItem(SCENE_KEY, t); } catch { /* ignore */ }
    };
    pendingCameraLockLabel = preset.name;
    if (dur > 0) {
      const easing = refs.morphEasing.value as EasingName;
      applyVizPreset(
        preset,
        renderer.params,
        setPresetScene,
        opts.include?.bg && background ? setBgFromPreset : null,
        (cs) => renderer.setAltCameraOverride(cs),
        opts,
      );
      const target: Partial<BioclastParams> = JSON.parse(JSON.stringify(renderer.params));
      Object.assign(renderer.params, morphSnapshot);
      startMorph(morphState, renderer.params, target, dur, easing);
    } else {
      applyVizPreset(
        preset,
        renderer.params,
        setPresetScene,
        opts.include?.bg && background ? setBgFromPreset : null,
        (cs) => renderer.setAltCameraOverride(cs),
        opts,
      );
    }
    pendingCameraLockLabel = null;
    setRecipeStatus(statusText ?? `Look: ${preset.name}`);
    lookPanel.refresh();
    morphSnapshot = JSON.parse(JSON.stringify(renderer.params));
  };
  const recipeIntent = (preset: VizPreset): string => {
    if (preset.sceneType !== "bioclast") return "Alt scene recipe";
    if (preset.group === "Audio Tunnel") return "Cinematic look";
    return "Bioclast look";
  };
  const recipeChips = (preset: VizPreset): string[] => {
    const keys = Object.keys(preset.params);
    const extras = VIZZ_EXTRAS[preset.vizzId];
    const chips = ["Look"];
    if (preset.sceneType !== "bioclast" || keys.some((k) => k.startsWith("sphere") || k.startsWith("meshGrid"))) chips.push("Scene");
    if (keys.some((k) => k.startsWith("fx"))) chips.push("FX");
    if (preset.bg || extras?.bg || extras?.bgSecondary) chips.push("Background");
    if (extras?.camera) chips.push("Camera");
    return chips;
  };
  const addRecipeAction = (
    parent: HTMLElement,
    label: string,
    preset: VizPreset,
    options: ApplyOptions,
    statusText: string,
  ): void => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "vizzLibraryItem__action";
    button.textContent = label;
    button.addEventListener("click", () => applyPreset(preset, options, statusText));
    parent.appendChild(button);
  };
  const renderLibrary = (): void => {
    const filter = refs.vizzLibraryGroup.value;
    refs.vizzLibraryList.innerHTML = "";
    for (const preset of VIZZ_PRESETS) {
      if (filter && preset.group !== filter) continue;
      const item = document.createElement("article");
      item.className = "vizzLibraryItem";
      item.title = `vizz.fm id: ${preset.vizzId}`;
      const main = document.createElement("div");
      const groupEl = document.createElement("div");
      groupEl.className = "vizzLibraryItem__group";
      groupEl.textContent = `${preset.group} - ${recipeIntent(preset)}`;
      const nameEl = document.createElement("div");
      nameEl.className = "vizzLibraryItem__name";
      nameEl.textContent = preset.name;
      main.appendChild(groupEl);
      main.appendChild(nameEl);
      item.appendChild(main);
      const chips = document.createElement("div");
      chips.className = "vizzLibraryItem__chips";
      for (const chip of recipeChips(preset)) {
        const el = document.createElement("span");
        el.className = "vizzLibraryItem__chip";
        el.textContent = chip;
        chips.appendChild(el);
      }
      item.appendChild(chips);
      const actions = document.createElement("div");
      actions.className = "vizzLibraryItem__actions";
      addRecipeAction(actions, "Apply Look", preset, lookOnlyOptions(), `Look: ${preset.name}`);
      if (preset.sceneType !== "bioclast") {
        addRecipeAction(actions, "Scene + Look", preset, sceneLookOptions(), `Scene + look: ${preset.name}`);
      }
      addRecipeAction(actions, "Full Recipe", preset, fullRecipeOptions(), `Recipe: ${preset.name}`);
      item.appendChild(actions);
      refs.vizzLibraryList.appendChild(item);
    }
  };
  refs.vizzLibraryGroup.addEventListener("change", renderLibrary);
  refs.vizzLibraryRandom.addEventListener("click", () => {
    const filter = refs.vizzLibraryGroup.value;
    const pool = filter ? VIZZ_PRESETS.filter((p) => p.group === filter) : VIZZ_PRESETS;
    if (!pool.length) return;
    const preset = pool[Math.floor(Math.random() * pool.length)];
    applyPreset(preset, lookOnlyOptions(), `Look: ${preset.name}`);
  });
  renderLibrary();

  // -------- Image Overlay --------
  const imgOverlay = new ImageOverlay(document.body);
  // Sync UI with persisted state on init.
  refs.imgOverlay.visible.checked = imgOverlay.cfg.visible;
  refs.imgOverlay.opacity.value = String(imgOverlay.cfg.opacity);
  refs.imgOverlay.blend.value = imgOverlay.cfg.blendMode;
  refs.imgOverlay.scale.value = String(imgOverlay.cfg.scale);
  refs.imgOverlay.posX.value = String(imgOverlay.cfg.posX);
  refs.imgOverlay.posY.value = String(imgOverlay.cfg.posY);
  refs.imgOverlay.upload.addEventListener("change", () => {
    const f = refs.imgOverlay.upload.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (): void => {
      imgOverlay.setSrc(typeof reader.result === "string" ? reader.result : null);
      if (!imgOverlay.cfg.visible) {
        refs.imgOverlay.visible.checked = true;
        imgOverlay.update({ visible: true });
      }
    };
    reader.readAsDataURL(f);
  });
  refs.imgOverlay.clear.addEventListener("click", () => {
    imgOverlay.setSrc(null);
    refs.imgOverlay.visible.checked = false;
    imgOverlay.update({ visible: false });
  });
  refs.imgOverlay.visible.addEventListener("change", () => imgOverlay.update({ visible: refs.imgOverlay.visible.checked }));
  refs.imgOverlay.opacity.addEventListener("input", () => imgOverlay.update({ opacity: parseFloat(refs.imgOverlay.opacity.value) }));
  refs.imgOverlay.blend.addEventListener("change", () => imgOverlay.update({ blendMode: refs.imgOverlay.blend.value as GlobalCompositeOperation | "normal" }));
  refs.imgOverlay.scale.addEventListener("input", () => imgOverlay.update({ scale: parseFloat(refs.imgOverlay.scale.value) }));
  refs.imgOverlay.posX.addEventListener("input", () => imgOverlay.update({ posX: parseFloat(refs.imgOverlay.posX.value) }));
  refs.imgOverlay.posY.addEventListener("input", () => imgOverlay.update({ posY: parseFloat(refs.imgOverlay.posY.value) }));

  // RecordingCompositor — sees both the WebGL canvas and every overlay
  // (Now Playing text, watermark, image overlay) so they all bake into
  // recordings + snapshots. Without this, `canvas.captureStream()` would
  // capture the visualizer alone and silently drop every DOM overlay.
  const compositor = new RecordingCompositor(renderer.getCanvas(), {
    nowPlaying: overlay,
    imageOverlay: imgOverlay,
  });

  // -------- Presets --------
  const formatSavedAt = (iso: string): string => {
    try {
      const d = new Date(iso);
      const now = Date.now();
      const diffMin = Math.floor((now - d.getTime()) / 60000);
      if (diffMin < 1) return "just now";
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffH = Math.floor(diffMin / 60);
      if (diffH < 24) return `${diffH}h ago`;
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  const refreshPresetList = (): void => {
    refs.presetList.innerHTML = "";
    const items = presets.list();
    if (items.length === 0) {
      const opt = document.createElement("option");
      opt.value = "";
      opt.textContent = "(no presets saved yet)";
      opt.disabled = true;
      opt.selected = true;
      refs.presetList.appendChild(opt);
      return;
    }
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "Load preset…";
    placeholder.disabled = true;
    placeholder.selected = true;
    refs.presetList.appendChild(placeholder);
    // Most-recently-saved first.
    items.sort((a, b) => b.savedAt.localeCompare(a.savedAt));
    for (const p of items) {
      const opt = document.createElement("option");
      opt.value = p.name;
      opt.textContent = `${p.name} · ${formatSavedAt(p.savedAt)}`;
      refs.presetList.appendChild(opt);
    }
  };
  refreshPresetList();

  refs.savePreset.addEventListener("click", () => {
    const existing = presets.list();
    const suggested =
      existing.length === 0 ? "My look" : `My look ${existing.length + 1}`;
    const name = window.prompt(
      "Save current look as preset:\n(All visual settings are stored.)",
      suggested,
    );
    if (!name) return;
    presets.upsert(name.trim(), renderer.params);
    refreshPresetList();
  });

  refs.presetList.addEventListener("change", () => {
    const name = refs.presetList.value;
    if (!name) return;
    const p = presets.get(name);
    if (!p) return;
    Object.assign(renderer.params, p.params);
    lookPanel.refresh();
    // Reset the dropdown back to the placeholder so the user can re-pick the
    // same preset and feel the snap (a "load again" button would be nicer
    // long-term).
    refs.presetList.value = "";
  });

  refs.exportPresets.addEventListener("click", () => {
    const blob = new Blob([presets.exportJson()], { type: "application/json" });
    downloadBlob(blob, "pulsefx-presets.json");
  });

  // -------- Share link (vizz.fm-style ?preset=<base64> deep link) --------
  // Builds a URL that bakes the *current* params into the query string so a
  // recipient lands on the exact look. Tries the native share sheet first
  // (Web Share API + Capacitor), then clipboard, then a visible prompt.
  let shareStatusTimer: number | null = null;
  const setShareStatus = (msg: string): void => {
    refs.sharePresetStatus.textContent = msg;
    if (shareStatusTimer !== null) window.clearTimeout(shareStatusTimer);
    if (!msg) return;
    shareStatusTimer = window.setTimeout(() => {
      refs.sharePresetStatus.textContent = "";
      shareStatusTimer = null;
    }, 4000);
  };
  refs.sharePresetLink.addEventListener("click", async () => {
    const url = buildShareUrl(renderer.params);
    // Try Web Share API first — best on mobile (one-tap to Messages, IG, etc.)
    const nav = navigator as Navigator & {
      share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
    };
    if (typeof nav.share === "function") {
      try {
        await nav.share({
          title: "PulseFX look",
          text: "Check out this PulseFX visualizer look",
          url,
        });
        setShareStatus("Shared.");
        return;
      } catch (err) {
        // AbortError = user dismissed; fall through to clipboard either way.
        if ((err as { name?: string })?.name !== "AbortError") {
          // share failed for a real reason — still try clipboard
        } else {
          setShareStatus("");
          return;
        }
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareStatus("Link copied to clipboard.");
    } catch {
      // Clipboard blocked (insecure context, permission denied) — show the
      // URL so the user can copy it manually.
      window.prompt("Copy this share link:", url);
      setShareStatus("");
    }
  });

  refs.importPresets.addEventListener("change", async () => {
    const f = refs.importPresets.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      const n = presets.importJson(text);
      refreshPresetList();
      setStatus(`Imported ${n} preset(s).`);
    } catch (err) {
      setStatus(
        `Import failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      refs.importPresets.value = "";
    }
  });

  // -------- Recording --------
  const setRecStatus = (msg: string): void => {
    refs.recordStatus.textContent = msg;
  };
  const recordLabel = refs.recordBtn.querySelector(
    ".reelStudio__label",
  ) as HTMLElement | null;
  const setRecordLabel = (text: string): void => {
    if (recordLabel) recordLabel.textContent = text;
    else refs.recordBtn.textContent = text;
  };

  // -------- Reel Studio: aspect frame --------
  // Switching the frame's data-aspect attribute resizes the canvas via CSS,
  // and Renderer already listens to window resize so the WebGL viewport
  // follows. We trigger a synthetic resize so it picks up the change without
  // waiting for the user to actually resize the window.
  refs.aspectSelect.addEventListener("change", () => {
    refs.frame.dataset.aspect = refs.aspectSelect.value;
    window.dispatchEvent(new Event("resize"));
  });

  // -------- Reel Studio: Quick Start (per-platform recipes) --------
  // Each preset configures aspect + reel length + auto-cycle concepts +
  // autopilot interval in one tap. The canonical platform list lives in
  // `record/socialPresets.ts` so mobile + desktop stay in lockstep and
  // filename / share metadata also flow from the same source.
  //
  // When a preset is armed we also surface an inline pill ("Armed:
  // TikTok · 9:16 · 30s") and collapse the redundant aspect/length
  // selects under a `<details class="reelCustom">` disclosure so the
  // recording controls don't show two contradictory truths.
  const armedPill = document.getElementById("quickStartArmed");
  const armedName = document.getElementById("quickStartArmedName");
  const armedSpec = document.getElementById("quickStartArmedSpec");
  const armedClear = document.getElementById("quickStartClear") as HTMLButtonElement | null;
  const reelCustom = document.getElementById("reelCustom") as HTMLDetailsElement | null;
  const aspectLabel = (a: string): string => {
    if (a === "portrait") return "9:16";
    if (a === "square") return "1:1";
    if (a === "landscape") return "16:9";
    return "fit";
  };
  const showArmedUI = (presetId: string, presetLabel: string, aspect: string, lengthSec: number): void => {
    if (armedPill && armedName && armedSpec) {
      armedName.textContent = presetLabel;
      const lengthText = lengthSec > 0 ? `${lengthSec}s` : "no cap";
      armedSpec.textContent = ` · ${aspectLabel(aspect)} · ${lengthText}`;
      armedPill.hidden = false;
    }
    // Collapse Custom aspect/length disclosure since the preset is in charge.
    if (reelCustom) reelCustom.open = false;
    void presetId;
  };
  const hideArmedUI = (): void => {
    if (armedPill) armedPill.hidden = true;
  };
  let armedSocialPresetId: string | null = null;
  document
    .querySelectorAll<HTMLButtonElement>(".quickStart [data-quickstart]")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        const key = btn.dataset.quickstart;
        const preset = getSocialPreset(key);
        if (!preset) return;
        // Aspect
        refs.aspectSelect.value = preset.aspect;
        refs.frame.dataset.aspect = preset.aspect;
        window.dispatchEvent(new Event("resize"));
        // Length
        refs.reelLength.value = String(preset.lengthSec);
        // Auto-cycle concepts
        refs.autoCycle.checked = preset.autoCycle;
        if (preset.autoCycle) startAutoCycle();
        else stopAutoCycle();
        // Autopilot framing
        renderer.params.autopilotEnabled = preset.autopilot;
        renderer.params.autopilotInterval = preset.autopilotInterval;
        lookPanel.refresh();
        // Remember which platform is "armed" so the next saved file uses
        // the platform-aware filename slug (`pulsefx-tiktok-...mp4`).
        armedSocialPresetId = preset.id;
        // Visual feedback on the button.
        document
          .querySelectorAll<HTMLButtonElement>(".quickStart [data-quickstart]")
          .forEach((b) => b.classList.remove("is-armed"));
        btn.classList.add("is-armed");
        showArmedUI(preset.id, preset.label || preset.id, preset.aspect, preset.lengthSec);
      });
    });
  // Manually changing aspect or length unarms the platform — the saved
  // file then falls back to the generic `pulsefx-…` filename rather than
  // claiming a platform recipe the user has since edited.
  const unarmPreset = (): void => {
    armedSocialPresetId = null;
    document
      .querySelectorAll<HTMLButtonElement>(".quickStart [data-quickstart]")
      .forEach((b) => b.classList.remove("is-armed"));
    hideArmedUI();
  };
  refs.aspectSelect.addEventListener("change", unarmPreset);
  refs.reelLength.addEventListener("change", unarmPreset);
  if (armedClear) armedClear.addEventListener("click", unarmPreset);
  // Touch the canonical list so unused-import lints stay quiet on builds
  // where downstream callers don't reach for SOCIAL_PRESETS directly.
  void SOCIAL_PRESETS;

  // -------- Reel Studio: auto-cycle concepts --------
  // When recording a 30s clip you want the visuals to *evolve*, not stay on
  // one preset the whole time. Auto-cycle rotates through the concept modes
  // every ~6 seconds, refreshing the look panel so the active pad stays in
  // sync with the scene.
  let cycleTimer: ReturnType<typeof setInterval> | null = null;
  const cycleableConcepts: ConceptMode[] = CONCEPT_MODES.filter(
    (c) => c.value !== "none",
  ).map((c) => c.value);
  const startAutoCycle = (): void => {
    if (cycleTimer !== null) return;
    let i = cycleableConcepts.indexOf(renderer.params.conceptMode);
    if (i < 0) i = 0;
    cycleTimer = setInterval(() => {
      i = (i + 1) % cycleableConcepts.length;
      renderer.params.conceptMode = cycleableConcepts[i];
      lookPanel.refresh();
    }, 6000);
  };
  const stopAutoCycle = (): void => {
    if (cycleTimer !== null) {
      clearInterval(cycleTimer);
      cycleTimer = null;
    }
  };
  refs.autoCycle.addEventListener("change", () => {
    if (refs.autoCycle.checked) startAutoCycle();
    else stopAutoCycle();
  });

  // -------- Reel Studio: length cap + live timer --------
  let recordingStartedAt = 0;
  let lengthCapMs = 0;
  let clipCapMs = 0;
  let timerHandle: ReturnType<typeof setInterval> | null = null;
  const tickTimer = (): void => {
    if (!recorder.isRecording()) return;
    const elapsedMs = performance.now() - recordingStartedAt;
    const elapsedSec = elapsedMs / 1000;
    const activeCapMs =
      lengthCapMs > 0 && clipCapMs > 0
        ? Math.min(lengthCapMs, clipCapMs)
        : Math.max(lengthCapMs, clipCapMs);
    if (activeCapMs > 0) {
      const remaining = Math.max(0, (activeCapMs - elapsedMs) / 1000);
      setRecStatus(
        `● REC  ${formatTime(elapsedSec)}  /  ${formatTime(
          activeCapMs / 1000,
        )}  (−${formatTime(remaining)})`,
      );
      if (elapsedMs >= activeCapMs) {
        void toggleRecord();
      }
    } else {
      setRecStatus(`● REC  ${formatTime(elapsedSec)}`);
    }
  };

  const toggleRecord = async (): Promise<void> => {
    if (recorder.isRecording()) {
      try {
        const result = await recorder.stop();
        compositor.stop();
        const armed = getSocialPreset(armedSocialPresetId);
        downloadBlob(
          result.blob,
          armed
            ? suggestSocialFilename(result.extension, armed)
            : suggestFilename(result.extension),
        );
        setRecStatus(
          `Saved (${(result.blob.size / 1_000_000).toFixed(1)} MB, ` +
            `${(result.durationMs / 1000).toFixed(1)}s).`,
        );
        setRecordLabel("Record reel");
        refs.recordBtn.classList.remove("recording");
        refs.recordStatus.classList.remove("recording");
        if (timerHandle !== null) {
          clearInterval(timerHandle);
          timerHandle = null;
        }
      } catch (err) {
        setRecStatus(
          `Stop failed: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
      return;
    }

    try {
      const info = engine.info();
      const media = info.mediaElement;
      const useClip =
        refs.clipEnabled.checked &&
        media &&
        Number.isFinite(media.duration) &&
        media.duration > 0 &&
        clipEndSec > clipStartSec;
      // Audio for recording: prefer recordStream (file/preanalyzed), fall
      // back to the source MediaStream (mic/tab). None for "no source" mode.
      const audioStream = info.recordStream ?? info.stream;
      if (useClip && media) {
        media.currentTime = clipStartSec;
        void media.play().catch(() => {
          /* tap-to-play fallback */
        });
      }
      compositor.start();
      recorder.start({
        videoStream: compositor.captureStream(60),
        audioStream,
      });
      recordingStartedAt = performance.now();
      lengthCapMs = Number(refs.reelLength.value) * 1000;
      clipCapMs = useClip ? (clipEndSec - clipStartSec) * 1000 : 0;
      setRecordLabel("Stop");
      refs.recordBtn.classList.add("recording");
      refs.recordStatus.classList.add("recording");
      tickTimer();
      if (timerHandle === null) {
        timerHandle = setInterval(tickTimer, 100);
      }
    } catch (err) {
      setRecStatus(
        `Start failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };
  refs.recordBtn.addEventListener("click", () => {
    void toggleRecord();
  });

  refs.snapshotBtn.addEventListener("click", async () => {
    const blob = await compositor.snapshot();
    if (blob) {
      downloadBlob(blob, suggestFilename("png"));
    } else {
      setRecStatus("Snapshot failed.");
    }
  });

  // -------- Live meters --------
  const setMeter = (el: HTMLElement, v: number): void => {
    el.style.width = `${Math.max(0, Math.min(1, v)) * 100}%`;
  };
  const meterLoop = (): void => {
    const f = engine.read();
    setMeter(refs.meters.bass, f.bass);
    setMeter(refs.meters.mid, f.mid);
    setMeter(refs.meters.treble, f.treble);
    setMeter(refs.meters.beat, f.beat);
    refs.bpm.textContent = `BPM: ${f.bpm > 0 ? f.bpm.toFixed(0) : "—"}`;
    requestAnimationFrame(meterLoop);
  };
  requestAnimationFrame(meterLoop);

  // -------- Sidebar navigation + scene gallery + Pad launchers --------
  setupSidebarTabs();
  setupSceneGallery();
  setupLegacyLibrary();
  setupOverlaySubTabs();
  setupPadLaunchers(deps.engine);
  setupPadsCategory(deps.engine);

  // -------- FX preset bundles --------
  const FX_PRESET_BUNDLES: Record<string, Partial<BioclastParams>> = {
    dreamy: { fxTiltShift: 0.5, fxVignette: 0.4, fxFilmGrain: 0.15, postFxEnabled: true },
    retro: { fxCrt: 0.8, fxFilmGrain: 0.6, fxVignette: 0.5, fxDither: 0.3, postFxEnabled: true },
    glitch: { fxGlitch: 0.7, fxRgbShift: 0.5, postFxEnabled: true },
    psychedelic: { fxKaleidoscope: 0.8, fxKaleidoscopeSegments: 8, fxSwirl: 0.4, fxRgbShift: 0.3, postFxEnabled: true },
    clean: {
      fxKaleidoscope: 0, fxSwirl: 0, fxBarrel: 0, fxGridTile: 0,
      fxPixelate: 0, fxGlitch: 0, fxRadialBlur: 0, fxRgbShift: 0,
      fxDotScreen: 0, fxDither: 0, fxCrt: 0, fxTiltShift: 0,
      fxVignette: 0, fxFilmGrain: 0,
    },
  };
  document.querySelectorAll<HTMLButtonElement>('.fxPresetBtn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const bundle = FX_PRESET_BUNDLES[btn.dataset.fxPreset ?? ''];
      if (!bundle) return;
      Object.assign(renderer.params, bundle);
      for (const r of fxRefreshers) r();
      refs.postFxEnabled.checked = renderer.params.postFxEnabled;
    });
  });

  // -------- Scene search (basic filter on library pane) --------
  document.getElementById('sceneSearch')?.addEventListener('input', (e) => {
    const q = (e.target as HTMLInputElement).value.trim().toLowerCase();
    // Only filter when library pane is active
    if (!q) {
      refs.vizzLibraryList.querySelectorAll<HTMLElement>('.vizzLibraryItem').forEach((el) => { el.hidden = false; });
      return;
    }
    refs.vizzLibraryList.querySelectorAll<HTMLElement>('.vizzLibraryItem').forEach((el) => {
      const text = el.textContent?.toLowerCase() ?? '';
      el.hidden = !text.includes(q);
    });
  });

  // -------- Keyboard shortcuts --------
  window.addEventListener("keydown", (ev) => {
    if (ev.target instanceof HTMLInputElement) return;
    if (ev.key === "r" || ev.key === "R") {
      void toggleRecord();
    } else if (ev.key === "c" || ev.key === "C") {
      // Recenter the camera: snap orbit, tilt, zoom back to defaults.
      renderer.params.cameraOrbit = 0;
      renderer.params.cameraTilt = 0.55;
      renderer.params.cameraZoom = 1.0;
      lookPanel.refresh();
    } else if (ev.key === "f" || ev.key === "F") {
      if (document.fullscreenElement) {
        void document.exitFullscreen();
      } else {
        void document.documentElement.requestFullscreen();
      }
    } else if (ev.key === "h" || ev.key === "H") {
      document.body.classList.toggle("ui-hidden");
    } else if (ev.key === " " || ev.code === "Space") {
      const el = engine.info().mediaElement;
      if (el) {
        ev.preventDefault();
        if (el.paused) {
          void el.play();
        } else {
          el.pause();
        }
      }
    }
  });
}

/** Format seconds as `M:SS` (or `S.0` for sub-minute durations). */
function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  if (sec < 60) return `${sec.toFixed(1)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec - m * 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

const OVERLAY_LS_KEY = "pulsefx.overlay";

function loadOverlayConfig(): OverlayConfig | null {
  try {
    const raw = localStorage.getItem(OVERLAY_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OverlayConfig>;
    return { ...DEFAULT_OVERLAY, ...parsed };
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar tab navigation
// ─────────────────────────────────────────────────────────────────────────────

function setupSidebarTabs(): void {
  const tabs = document.querySelectorAll<HTMLButtonElement>('.sidebarTab');
  const panels = document.querySelectorAll<HTMLElement>('.sidebarPanel');

  const activateTab = (tabId: string): void => {
    tabs.forEach((t) => {
      t.classList.toggle('is-active', t.dataset.tab === tabId);
      t.setAttribute('aria-selected', t.dataset.tab === tabId ? 'true' : 'false');
    });
    panels.forEach((p) => {
      p.hidden = p.dataset.panel !== tabId;
    });
    try { localStorage.setItem('pfx_sidebar_tab', tabId); } catch { /* ignore */ }
  };

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => activateTab(tab.dataset.tab ?? 'scene'));
  });

  // Keyboard shortcuts 1–6 (skip when focus is in a text input/textarea)
  document.addEventListener('keydown', (e) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    const tabMap: Record<string, string> = {
      '1': 'source', '2': 'scene', '3': 'style',
      '4': 'motion', '5': 'overlay', '6': 'reel',
    };
    if (tabMap[e.key]) {
      e.preventDefault();
      activateTab(tabMap[e.key]);
    }
  });

  // Restore saved tab; default to 'scene'
  let saved = 'scene';
  try { saved = localStorage.getItem('pfx_sidebar_tab') ?? 'scene'; } catch { /* ignore */ }
  activateTab(saved);
}

type CameraSliderKey =
  | "cameraZoom"
  | "cameraTilt"
  | "cameraOrbit"
  | "cameraSpin"
  | "flythroughSpeed"
  | "beatDolly"
  | "autopilotInterval";

type CameraToggleKey = "autopilotEnabled" | "autopilotBeatLock";

interface CameraSliderSpec {
  label: string;
  sub: string;
  key: CameraSliderKey;
  min: number;
  max: number;
  step: number;
  left: string;
  right: string;
}

const CAMERA_SLIDERS: CameraSliderSpec[] = [
  { label: "Zoom", sub: "distance from scene", key: "cameraZoom", min: 0.15, max: 3.5, step: 0.01, left: "way out", right: "close" },
  { label: "Tilt", sub: "vertical angle", key: "cameraTilt", min: 0, max: 1, step: 0.01, left: "horizon", right: "top-down" },
  { label: "Manual orbit", sub: "yaw offset", key: "cameraOrbit", min: -Math.PI, max: Math.PI, step: 0.01, left: "left", right: "right" },
  { label: "Auto spin", sub: "orbit speed", key: "cameraSpin", min: 0, max: 3, step: 0.01, left: "still", right: "fast" },
  { label: "Flythrough speed", sub: "spaceship pace", key: "flythroughSpeed", min: 0.1, max: 3, step: 0.01, left: "drift", right: "warp" },
  { label: "Beat dolly", sub: "punch on each beat", key: "beatDolly", min: 0, max: 1, step: 0.01, left: "off", right: "punchy" },
  { label: "Autopilot interval", sub: "seconds per shot", key: "autopilotInterval", min: 2, max: 30, step: 1, left: "fast", right: "slow" },
];

const CAMERA_FRAMING_LABELS: Record<string, string> = {
  wide: "Wide",
  hero: "Hero",
  topdown: "Top-Down",
  cinema: "Cinematic",
  closeup: "Closeup",
  "fly-slow": "Fly · Slow",
  "fly-cruise": "Fly · Cruise",
  "fly-warp": "Fly · Warp",
};

function setupCameraFramingPanel(
  renderer: Renderer,
  refreshLookPanel: () => void,
): () => void {
  const section = document.getElementById("cameraFramingSection");
  const sliderHost = document.getElementById("cameraFramingSliders");
  if (!section || !sliderHost) return () => { /* no camera panel on this page */ };

  const modeHost = section.querySelector<HTMLElement>(".cameraModeToggle");
  const padsHost = section.querySelector<HTMLElement>(".cameraFraming__pads");
  if (modeHost && !modeHost.querySelector("button")) {
    for (const mode of ["orbit", "flythrough"] as const) {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.cameraMode = mode;
      button.textContent = mode === "orbit" ? "Orbit" : "Flythrough";
      modeHost.appendChild(button);
    }
  }
  if (padsHost && !padsHost.querySelector("button")) {
    for (const preset of FRAMING_PRESETS) {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.framing = preset.value;
      button.textContent = CAMERA_FRAMING_LABELS[preset.value] ?? preset.value;
      padsHost.appendChild(button);
    }
  }

  const modeButtons = Array.from(
    section.querySelectorAll<HTMLButtonElement>("[data-camera-mode]"),
  );
  const padButtons = Array.from(
    section.querySelectorAll<HTMLButtonElement>("[data-framing]"),
  );
  const sliderRows: Array<{
    spec: CameraSliderSpec;
    input: HTMLInputElement;
    value: HTMLElement;
  }> = [];
  const toggleRows: Array<{ key: CameraToggleKey; input: HTMLInputElement }> = [];

  const formatValue = (key: CameraSliderKey, value: number): string => {
    if (key === "cameraOrbit") return value.toFixed(2);
    if (key === "autopilotInterval") return `${Math.round(value)}s`;
    return value.toFixed(2);
  };

  const refresh = (): void => {
    const params = renderer.params;
    modeButtons.forEach((button) => {
      const isActive = button.dataset.cameraMode === params.cameraMode;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-checked", isActive ? "true" : "false");
    });
    sliderRows.forEach(({ spec, input, value }) => {
      const current = params[spec.key];
      input.value = String(current);
      value.textContent = formatValue(spec.key, current);
    });
    toggleRows.forEach(({ key, input }) => {
      input.checked = params[key];
    });
  };

  modeButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const mode = button.dataset.cameraMode;
      if (mode !== "orbit" && mode !== "flythrough") return;
      renderer.params.cameraMode = mode;
      refreshLookPanel();
    });
  });

  padButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const preset = FRAMING_PRESETS.find(
        (candidate) => candidate.value === button.dataset.framing,
      );
      if (!preset) return;
      Object.assign(renderer.params, preset.values);
      refreshLookPanel();
    });
  });

  sliderHost.innerHTML = "";
  const slidersWrap = document.createElement("div");
  slidersWrap.className = "cameraFraming__controlList";
  sliderHost.appendChild(slidersWrap);

  for (const spec of CAMERA_SLIDERS) {
    const row = document.createElement("label");
    row.className = "cameraFraming__control";

    const header = document.createElement("span");
    header.className = "cameraFraming__controlHeader";
    const label = document.createElement("span");
    label.textContent = spec.label;
    const value = document.createElement("span");
    value.className = "cameraFraming__value";
    header.append(label, value);

    const sub = document.createElement("span");
    sub.className = "cameraFraming__sub";
    sub.textContent = spec.sub;

    const input = document.createElement("input");
    input.type = "range";
    input.min = String(spec.min);
    input.max = String(spec.max);
    input.step = String(spec.step);
    input.addEventListener("input", () => {
      renderer.params[spec.key] = Number(input.value);
      refreshLookPanel();
    });

    const scale = document.createElement("span");
    scale.className = "cameraFraming__scale";
    const left = document.createElement("span");
    left.textContent = spec.left;
    const right = document.createElement("span");
    right.textContent = spec.right;
    scale.append(left, right);

    row.append(header, sub, input, scale);
    slidersWrap.appendChild(row);
    sliderRows.push({ spec, input, value });
  }

  const togglesWrap = document.createElement("div");
  togglesWrap.className = "cameraFraming__toggles";
  sliderHost.appendChild(togglesWrap);

  const addToggle = (labelText: string, subText: string, key: CameraToggleKey): void => {
    const label = document.createElement("label");
    label.className = "toggleRow cameraFraming__toggle";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.addEventListener("change", () => {
      renderer.params[key] = input.checked;
      refreshLookPanel();
    });
    const body = document.createElement("span");
    body.innerHTML = `<strong>${labelText}</strong><small>${subText}</small>`;
    label.append(input, body);
    togglesWrap.appendChild(label);
    toggleRows.push({ key, input });
  };

  addToggle("Autopilot framing", "auto-cycles framing presets", "autopilotEnabled");
  addToggle("Lock changes to beats", "snap on next beat", "autopilotBeatLock");

  refresh();
  return refresh;
}

function setupSceneGallery(): void {
  const cats = document.querySelectorAll<HTMLButtonElement>('.sceneCat');
  const tiers = document.querySelectorAll<HTMLButtonElement>('.sceneTier');
  const panes = document.querySelectorAll<HTMLElement>('.scenePane');
  const hint = document.getElementById('sceneCatHint');
  const validCats = new Set(Array.from(cats).map((c) => c.dataset.cat ?? ''));
  const validTiers = new Set(Array.from(tiers).map((tier) => tier.dataset.sceneTier ?? ''));

  // Short descriptions shown under the category bar so users know what
  // each category contains without having to click into it first.
  const HINTS: Record<string, string> = {
    pulsefx: 'Curated PulseFX particle scenes — moods, variants, fine-tuning sliders.',
    styles: 'Switch the underlying render engine. Each engine has a different visual language.',
    library: 'Vizz.fm-style preset recipes. Pick how much of each recipe to apply.',
    pads: 'Universal pad container — pick any WebGL pad, tweak its live audio-reactive sliders.',
    opensource: 'Open Source Lab — Reactivity Lab, Shadertoy GLSL, CodePen.io HTML/CSS/JS, Butterchurn, and Song Galaxy Studio stay isolated from the release scene engine.',
    legacy: '8,000+ open-source AVS / MilkDrop visualizers + Butterchurn, Shaders, Galaxy popouts. Star ★ to keep.',
  };

  const activateTier = (tierId: string): void => {
    if (!validTiers.has(tierId)) tierId = 'core';
    tiers.forEach((tier) => {
      tier.classList.toggle('is-active', tier.dataset.sceneTier === tierId);
    });
    try { localStorage.setItem('pfx_scene_tier', tierId); } catch { /* ignore */ }
  };

  const activateCat = (cat: string): void => {
    if (!validCats.has(cat)) cat = 'pulsefx';
    cats.forEach((c) => c.classList.toggle('is-active', c.dataset.cat === cat));
    panes.forEach((p) => { p.hidden = p.dataset.pane !== cat; });
    if (hint && HINTS[cat]) hint.textContent = HINTS[cat];
    activateTier(cat === 'opensource' ? 'lab' : 'core');
    document.dispatchEvent(new CustomEvent('pfx:sceneCat', { detail: { cat } }));
    try { localStorage.setItem('pfx_scene_cat', cat); } catch { /* ignore */ }
  };

  cats.forEach((cat) => {
    cat.addEventListener('click', () => activateCat(cat.dataset.cat ?? 'pulsefx'));
  });

  tiers.forEach((tier) => {
    tier.addEventListener('click', () => {
      const tierId = tier.dataset.sceneTier ?? 'core';
      activateCat(tierId === 'lab' ? 'opensource' : 'pulsefx');
      activateTier(tierId);
    });
  });

  let saved = 'pulsefx';
  try { saved = localStorage.getItem('pfx_scene_cat') ?? 'pulsefx'; } catch { /* ignore */ }
  activateCat(saved);
}

// ─────────────────────────────────────────────────────────────────────────────
// Legacy library: favorites list + browse-button wiring
//
// Favorites are written by the standalone preset previewer
// (docs/extracted-avs/previewer.html) into localStorage[FAV_KEY]. When the
// previewer runs on the same origin as PulseFX, favorites sync automatically
// via the storage event. For different-origin setups (e.g. local previewer
// on :9090, PulseFX on Pages), users export/import JSON.
// ─────────────────────────────────────────────────────────────────────────────

const LEGACY_FAV_KEY = 'pulsefx.legacyFavorites';
const LEGACY_LIB_URL_KEY = 'pulsefx.legacyLibraryUrl';
const LEGACY_LIB_DEFAULT = 'http://127.0.0.1:9090/docs/extracted-avs/previewer.html';

interface LegacyFavRecord {
  key: string;
  name: string;
  fmt: 'milk' | 'avs' | 'builtin';
  pack: string;
  url?: string;
  added: number;
}

function loadLegacyFavorites(): LegacyFavRecord[] {
  try {
    const raw = localStorage.getItem(LEGACY_FAV_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function saveLegacyFavorites(records: LegacyFavRecord[]): void {
  try { localStorage.setItem(LEGACY_FAV_KEY, JSON.stringify(records)); } catch { /* ignore */ }
}

function importedLegacyKey(name: string): string {
  return 'imported:' + name.toLowerCase().replace(/^[★☆\s"']+/, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function parseLegacyFavoritesInput(input: string): LegacyFavRecord[] {
  try {
    const arr = JSON.parse(input);
    if (!Array.isArray(arr)) throw new Error('Expected an array');
    return arr
      .filter((r) => r && typeof r.name === 'string')
      .map((r) => ({
        key: typeof r.key === 'string' ? r.key : importedLegacyKey(r.name),
        name: r.name,
        fmt: r.fmt === 'avs' || r.fmt === 'builtin' ? r.fmt : 'milk',
        pack: typeof r.pack === 'string' ? r.pack : 'Legacy',
        url: typeof r.url === 'string' ? r.url : '',
        added: typeof r.added === 'number' ? r.added : Date.now(),
      }));
  } catch {
    return input.split(/\n+/)
      .map((line) => line.replace(/^[★☆\s]+/, '').trim())
      .filter(Boolean)
      .map((name) => ({
        key: importedLegacyKey(name),
        name,
        fmt: 'milk',
        pack: 'Imported name list',
        url: '',
        added: Date.now(),
      }));
  }
}

function mergeLegacyFavorites(records: LegacyFavRecord[]): LegacyFavRecord[] {
  const existing = loadLegacyFavorites();
  const seen = new Set(existing.map(r => r.key));
  const merged = [...existing];
  for (const r of records) {
    if (r && r.key && !seen.has(r.key)) { merged.push(r); seen.add(r.key); }
  }
  saveLegacyFavorites(merged);
  return merged;
}

function importLegacyFavoritesFromUrl(): boolean {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const params = new URLSearchParams(hash || window.location.search);
  const encoded = params.get('legacyFavorites');
  if (!encoded) return false;
  try {
    const records = parseLegacyFavoritesInput(encoded);
    mergeLegacyFavorites(records);
    const clean = window.location.pathname + window.location.search;
    window.history.replaceState(null, document.title, clean);
    return true;
  } catch {
    return false;
  }
}

function getLegacyLibraryUrl(): string {
  try {
    const url = localStorage.getItem(LEGACY_LIB_URL_KEY);
    if (url && /^https?:\/\//.test(url)) return url;
  } catch { /* ignore */ }
  return LEGACY_LIB_DEFAULT;
}

function setLegacyLibraryUrl(url: string): void {
  try { localStorage.setItem(LEGACY_LIB_URL_KEY, url); } catch { /* ignore */ }
}

function setupLegacyLibrary(): void {
  const list = document.getElementById('legacyFavoritesList');
  const empty = document.getElementById('legacyFavoritesEmpty');
  const count = document.getElementById('legacyFavoritesCount');
  const actions = document.getElementById('legacyFavoritesActions');
  const browseBtn = document.getElementById('legacyBrowse') as HTMLButtonElement | null;
  const importBtn = document.getElementById('legacyImport') as HTMLButtonElement | null;
  const syncBtn = document.getElementById('legacyFavoritesSync') as HTMLButtonElement | null;
  const clearBtn = document.getElementById('legacyFavoritesClear') as HTMLButtonElement | null;
  const urlInput = document.getElementById('legacyLibraryUrl') as HTMLInputElement | null;

  if (!list) return; // Pane not present (older HTML)

  const render = (): void => {
    const favs = loadLegacyFavorites();
    list.innerHTML = '';
    if (count) count.textContent = String(favs.length);
    if (empty) empty.style.display = favs.length ? 'none' : '';
    if (actions) actions.style.display = favs.length ? 'flex' : 'none';
    const sorted = [...favs].sort((a, b) => (b.added || 0) - (a.added || 0));
    for (const fav of sorted) {
      const li = document.createElement('li');
      li.className = 'legacyFavorites__item';
      li.title = `${fav.name} — ${fav.pack || 'Legacy'}`;
      const fmtSpan = document.createElement('span');
      fmtSpan.className = 'legacyFavorites__itemFmt ' + (fav.fmt === 'milk' ? 'fm' : fav.fmt === 'builtin' ? 'fb' : 'fa');
      fmtSpan.textContent = fav.fmt === 'milk' ? 'milk' : fav.fmt === 'builtin' ? 'built' : 'avs';
      const nameSpan = document.createElement('span');
      nameSpan.className = 'legacyFavorites__itemName';
      nameSpan.textContent = fav.name;
      const remove = document.createElement('button');
      remove.className = 'legacyFavorites__itemRemove';
      remove.type = 'button';
      remove.title = 'Remove favorite';
      remove.textContent = '✕';
      remove.addEventListener('click', (e) => {
        e.stopPropagation();
        const next = loadLegacyFavorites().filter(r => r.key !== fav.key);
        saveLegacyFavorites(next);
        render();
      });
      li.appendChild(fmtSpan);
      li.appendChild(nameSpan);
      li.appendChild(remove);
      li.addEventListener('click', () => {
        const base = getLegacyLibraryUrl();
        const sep = base.includes('?') ? '&' : '?';
        const param = fav.key.startsWith('imported:') || !fav.url
          ? `search=${encodeURIComponent(fav.name)}`
          : `preset=${encodeURIComponent(fav.key)}`;
        const url = `${base}${sep}${param}`;
        window.open(url, 'pulsefx-legacy-library');
      });
      list.appendChild(li);
    }
  };

  importLegacyFavoritesFromUrl();
  // Initial paint + sync on storage events from other tabs / the previewer
  render();
  window.addEventListener('storage', (e) => {
    if (e.key === LEGACY_FAV_KEY) render();
  });
  // Receive postMessage updates from the previewer when opened via window.open()
  window.addEventListener('message', (e) => {
    if (e.data && e.data.type === 'pulsefx.legacyFavorites' && Array.isArray(e.data.records)) {
      saveLegacyFavorites(e.data.records);
      render();
    }
  });

  // Wire up browse + URL input
  if (urlInput) {
    urlInput.value = getLegacyLibraryUrl();
    urlInput.addEventListener('change', () => setLegacyLibraryUrl(urlInput.value.trim() || LEGACY_LIB_DEFAULT));
  }
  browseBtn?.addEventListener('click', () => {
    window.open(getLegacyLibraryUrl(), 'pulsefx-legacy-library');
  });

  syncBtn?.addEventListener('click', () => render());

  importBtn?.addEventListener('click', () => {
    const text = prompt('Paste favorites JSON, or paste the plain ★ name list:');
    if (!text) return;
    try {
      const records = parseLegacyFavoritesInput(text);
      mergeLegacyFavorites(records);
      render();
    } catch (e) {
      alert('Import failed: ' + (e instanceof Error ? e.message : String(e)));
    }
  });

  clearBtn?.addEventListener('click', () => {
    if (!loadLegacyFavorites().length) return;
    if (!confirm('Remove all Legacy favorites? This cannot be undone.')) return;
    saveLegacyFavorites([]);
    render();
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Overlay panel: sub-tabs for Logo / Now Playing / Brand
// ─────────────────────────────────────────────────────────────────────────────

function setupOverlaySubTabs(): void {
  const tabs = document.querySelectorAll<HTMLButtonElement>('.overlaySubTab');
  const panels = document.querySelectorAll<HTMLElement>('.overlaySubPanel');
  if (!tabs.length || !panels.length) return;
  const validTabs = new Set(Array.from(tabs).map((t) => t.dataset.overlayTab ?? ''));
  const defaultTab = 'nowplaying';
  const tabStrip = document.querySelector<HTMLElement>('.overlaySubTabs');

  const activateTab = (tabId: string): void => {
    if (!validTabs.has(tabId)) tabId = defaultTab;
    tabs.forEach((t) => {
      const isOn = t.dataset.overlayTab === tabId;
      t.classList.toggle('is-active', isOn);
      t.setAttribute('aria-selected', isOn ? 'true' : 'false');
    });
    panels.forEach((p) => { p.hidden = p.dataset.overlayPanel !== tabId; });
    try { localStorage.setItem('pfx_overlay_subtab', tabId); } catch { /* ignore */ }
  };

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => activateTab(tab.dataset.overlayTab ?? defaultTab));
  });

  let saved = defaultTab;
  try { saved = localStorage.getItem('pfx_overlay_subtab') ?? defaultTab; } catch { /* ignore */ }
  if (tabStrip?.hasAttribute('hidden')) saved = defaultTab;
  activateTab(saved);
}

// ─────────────────────────────────────────────────────────────────────────────
// External pad launchers (Butterchurn, Shaders) — fullscreen iframe overlay
// with live audio bridged from the main analyzer via postMessage.
// ─────────────────────────────────────────────────────────────────────────────

interface PadDef { id: string; title: string; src: string; }
const PAD_DEFS: Record<string, PadDef> = {
  butterchurn: { id: "butterchurn", title: "Butterchurn", src: "/pads/butterchurn.html?ui=0" },
  shadertoy:   { id: "shadertoy",   title: "Shadertoy GLSL", src: "/pads/shadertoy.html?ui=0" },
  codepen:     { id: "codepen",     title: "CodePen.io Code Entry", src: "/pads/codepen.html?ui=0" },
  songGalaxy:  { id: "songGalaxy",  title: "Song Galaxy Studio", src: "/song-galaxy.html?embed=1" },
  reactivityLab: { id: "reactivityLab", title: "Reactivity Lab", src: "/reactivity-lab.html?embed=1&audio=bridge" },
  shaders:     { id: "shadertoy",   title: "Shaders (GLSL)", src: "/pads/shadertoy.html?ui=0" },
  galaxy:      { id: "galaxy",      title: "Galaxy (WebGPU)", src: "/pads/galaxy.html?embed=1&ui=0" },
};

interface OpenPadOverlayDetail { padId?: string; selectionId?: string | null; }

function setupPadLaunchers(engine: AudioEngine): void {
  const overlay = document.getElementById("padOverlay");
  const frame = document.getElementById("padFrame") as HTMLIFrameElement | null;
  const titleEl = document.getElementById("padOverlay__title");
  const closeBtn = document.getElementById("padOverlay__close");
  if (!overlay || !frame || !titleEl || !closeBtn) return;

  if (overlay.parentElement !== document.body) document.body.appendChild(overlay);
  Object.assign(overlay.style, {
    position: "fixed",
    inset: "0",
    zIndex: "2147483647",
    pointerEvents: "auto",
  });

  let activePad: string | null = null;
  let bridgeRunning = false;

  const buildOverlaySrc = (pad: PadDef, selectionId?: string | null): string => {
    const url = new URL(pad.src, window.location.href);
    if (!url.searchParams.has("ui")) url.searchParams.set("ui", "0");
    if (selectionId) {
      if (pad.id === "butterchurn") url.searchParams.set("preset", selectionId);
      else if (pad.id === "shadertoy" || pad.id === "codepen") url.searchParams.set("selection", selectionId);
    }
    return url.pathname + url.search + url.hash;
  };

  const broadcastAudio = (): void => {
    if (!activePad) { bridgeRunning = false; return; }
    try {
      if (frame.contentWindow) {
        const f = engine.read();
        frame.contentWindow.postMessage({
          type: "pfx:audio",
          features: {
            bass: f.bass, mid: f.mid, treble: f.treble, level: f.level,
            beat: f.beat, bpm: f.bpm, beatCount: f.beatCount,
            t: performance.now(),
          },
        }, "*");
      }
    } catch { /* cross-origin or detached frame */ }
    requestAnimationFrame(broadcastAudio);
  };

  const open = (padId: string, selectionId?: string | null): void => {
    const pad = PAD_DEFS[padId];
    if (!pad) return;
    activePad = padId;
    titleEl.textContent = pad.title;
    frame.src = buildOverlaySrc(pad, selectionId);
    overlay.hidden = false;
    overlay.setAttribute("aria-hidden", "false");
    if (!bridgeRunning) {
      bridgeRunning = true;
      requestAnimationFrame(broadcastAudio);
    }
  };

  const close = (): void => {
    activePad = null;
    bridgeRunning = false;
    overlay.hidden = true;
    overlay.setAttribute("aria-hidden", "true");
    frame.src = "about:blank";
  };

  closeBtn.addEventListener("click", close);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && activePad) close();
  });
  document.addEventListener("pfx:openPadOverlay", (event) => {
    const detail = (event as CustomEvent<OpenPadOverlayDetail>).detail ?? {};
    if (detail.padId) open(detail.padId, detail.selectionId ?? null);
  });
  document.addEventListener("pfx:closePadOverlay", close);

  document.getElementById("butterchurnLaunch")?.addEventListener("click", () => open("butterchurn"));
  document.getElementById("shadersLaunch")?.addEventListener("click", () => open("shaders"));
  document.getElementById("galaxyLaunch")?.addEventListener("click", () => open("galaxy"));
}

// ─────────────────────────────────────────────────────────────────────────────
// Pads category — mounts the universal PadContainer inside Open Source Lab.
// Tears down on switch away
// so iframes free GPU/audio. Audio is bridged from the live AudioEngine.
// ─────────────────────────────────────────────────────────────────────────────

function setupPadsCategory(engine: AudioEngine): void {
  const host = document.getElementById("padsContainerHost");
  const cats = document.querySelectorAll<HTMLButtonElement>(".sceneCat");
  const labButtons = document.querySelectorAll<HTMLButtonElement>("[data-lab-pad]");
  if (!host || !cats.length) return;

  const labPadIds = new Set(["reactivityLab", "shadertoy", "codepen", "butterchurn", "songGalaxy"]);
  const getSavedLabPad = (): string => {
    try {
      const savedPad = localStorage.getItem("pfx_lab_pad") ?? "";
      if (labPadIds.has(savedPad)) return savedPad;
    } catch { /* ignore */ }
    return "reactivityLab";
  };

  let handle: PadContainerHandle | null = null;
  let activeLabPad = getSavedLabPad();
  const activeSelections: Record<string, string> = {};

  const readSavedSelection = (padId: string): string | null => {
    try {
      const raw = localStorage.getItem("pfx-pc-pad-selection-v1");
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const value = parsed?.[padId];
      return typeof value === "string" && value ? value : null;
    } catch { return null; }
  };

  const getActiveSelection = (padId: string): string | null => activeSelections[padId] ?? readSavedSelection(padId);

  // Bridge live AudioEngine features → PadAudioFeatures shape (typed arrays
  // are dropped to keep TS strict happy; pads only need the scalars).
  const audioSource = (): PadAudioFeatures => {
    const f = engine.read();
    return {
      bass: f.bass,
      mid: f.mid,
      treble: f.treble,
      level: f.level,
      beat: f.beat,
      bpm: f.bpm,
      beatCount: f.beatCount,
      t: performance.now(),
    };
  };

  const syncLabButtons = (padId: string): void => {
    labButtons.forEach((button) => {
      button.classList.toggle("is-active", button.dataset.labPad === padId);
    });
  };

  const persistLabPad = (padId: string): void => {
    try { localStorage.setItem("pfx_lab_pad", padId); } catch { /* ignore */ }
  };

  const revealLabPad = (padId: string): void => {
    requestAnimationFrame(() => {
      const drawer = host.querySelector<HTMLElement>(".pfx-pc-drawer");
      (drawer ?? host).scrollIntoView({ block: "nearest", inline: "nearest" });
      if (padId === "codepen") {
        const editor = host.querySelector<HTMLElement>(".pfx-pc-special textarea, .pfx-pc-special input");
        editor?.focus({ preventScroll: true });
      }
    });
  };

  const mount = (padId = activeLabPad): void => {
    activeLabPad = padId;
    if (handle) return;
    try {
      handle = createPadContainer({
        root: host,
        layout: "desktop",
        includePadIds: ["reactivityLab", "shadertoy", "codepen", "butterchurn", "songGalaxy"],
        initialPadId: padId,
        audioSource,
        onPadChange: (pad) => {
          activeLabPad = pad.id;
          syncLabButtons(pad.id);
          persistLabPad(pad.id);
          revealLabPad(pad.id);
        },
        onSelectionChange: (padId, selectionId) => {
          activeSelections[padId] = selectionId;
        },
      });
      syncLabButtons(padId);
    } catch (err) {
      console.error("[pads] mount failed:", err);
    }
  };

  const selectLabPad = (padId: string): void => {
    if (!labPadIds.has(padId)) return;
    activeLabPad = padId;
    persistLabPad(padId);
    mount(padId);
    handle?.setPad(padId);
    syncLabButtons(padId);
    revealLabPad(padId);
  };

  const unmount = (): void => {
    if (!handle) return;
    try { handle.destroy(); } catch { /* ignore */ }
    handle = null;
    host.innerHTML = "";
  };

  cats.forEach((btn) => {
    btn.addEventListener("click", () => {
      if (btn.dataset.cat === "opensource") mount();
      else unmount();
    });
  });

  labButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const padId = button.dataset.labPad;
      if (!padId) return;
      selectLabPad(padId);
    });
  });

  document.getElementById("labPadLaunch")?.addEventListener("click", () => {
    mount(activeLabPad);
    document.dispatchEvent(new CustomEvent<OpenPadOverlayDetail>("pfx:openPadOverlay", {
      detail: { padId: activeLabPad, selectionId: getActiveSelection(activeLabPad) },
    }));
  });

  document.addEventListener("pfx:sceneCat", (event) => {
    const detail = (event as CustomEvent<{ cat?: string }>).detail;
    if (detail?.cat === "opensource") mount();
    else unmount();
  });

  // Mount on initial load if the saved category is Open Source Lab.
  let saved = "";
  try { saved = localStorage.getItem("pfx_scene_cat") ?? ""; } catch { /* ignore */ }
  if (saved === "opensource") {
    // Defer one frame so setupSceneGallery() finishes its activateCat() pass
    // and the pane becomes visible (otherwise iframe mounts hidden = 0 size).
    requestAnimationFrame(() => mount());
  }
}
